import React, { useState, useEffect } from 'react';
import { Autocomplete, Tabs, Tab, Box, TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, responsiveFontSizes, Typography, Alert } from '@mui/material';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAccesos } from '../contexts/AccesosContext';
import { Add, Email, Info, PrintOutlined, PrintTwoTone, WhatsApp, Visibility } from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import CondicionModal from './CondicionModal';  // Importar el componente del modal
import MuestraModal from './MuestraModal';
import UtilidadModal from './UtilidadModal';
import CantidadModal from './CantidadModal';
import ParametroModal from './ParametroModal';

import TituloVRModal from './TituloVRModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Importar el ícono de selección
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid'; // Asegúrate de instalar `uuid` con `npm install uuid`
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import { Description } from '@mui/icons-material';
import ComentariosModal from './ComentariosModal';
import Swal from 'sweetalert2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import TableChartIcon from '@mui/icons-material/TableChart';

// Funciones de utilidad para filtrado optimizado
const normalizeText = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remover acentos
        .trim();
};

const createSmartFilter = (searchFields, maxResults = 100) => {
    return (options, { inputValue }) => {
        if (!inputValue || inputValue.length < 1) {
            return options.slice(0, Math.min(maxResults, 50)); // Máximo 50 sin búsqueda
        }
        
        const normalizedInput = normalizeText(inputValue);
        const words = normalizedInput.split(/\s+/).filter(word => word.length > 0);
        
        if (words.length === 0) return options.slice(0, Math.min(maxResults, 50));
        
        const scoredOptions = options
            .map(option => {
                let score = 0;
                let matchCount = 0;
                
                // Evaluar cada campo de búsqueda
                searchFields.forEach(field => {
                    const fieldValue = normalizeText(typeof field === 'function' ? field(option) : option[field]);
                    if (!fieldValue) return;
                    
                    words.forEach(word => {
                        if (word.length < 1) return; // Permitir palabras de 1 carácter (como símbolos)
                        
                        // Coincidencia exacta al inicio (máxima puntuación)
                        if (fieldValue.startsWith(word)) {
                            score += 1000;
                            matchCount++;
                        }
                        // Coincidencia exacta al inicio de cualquier palabra
                        else if (fieldValue.split(/\s+/).some(w => w.startsWith(word))) {
                            score += 500;
                            matchCount++;
                        }
                        // Coincidencia de palabra completa
                        else if (fieldValue.split(/\s+/).some(w => w === word)) {
                            score += 200;
                            matchCount++;
                        }
                        // Contiene la palabra (menor puntuación)
                        else if (fieldValue.includes(word)) {
                            score += 50;
                            matchCount++;
                        }
                    });
                });
                
                // Bonus si todas las palabras coinciden
                if (matchCount >= words.length) {
                    score += 100;
                }
                
                // Penalización por longitud excesiva (preferir opciones más cortas y relevantes)
                const totalLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(option) : option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                if (totalLength > 100) {
                    score = Math.max(0, score - (totalLength - 100) * 2);
                }
                
                return { option, score };
            })
            .filter(item => item.score > 0)
            .sort((a, b) => {
                // Priorizar por score, y en caso de empate, por longitud del texto
                if (b.score !== a.score) {
                    return b.score - a.score;
                }
                
                // En caso de empate, preferir textos más cortos
                const aLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(a.option) : a.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                const bLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(b.option) : b.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                return aLength - bLength;
            })
            .slice(0, maxResults)
            .map(item => item.option);
            
        return scoredOptions;
    };
};

// Filtro específico para casos donde queremos búsqueda muy restrictiva
const createRestrictiveFilter = (searchFields, maxResults = 15) => {
    return (options, { inputValue }) => {
        if (!inputValue || inputValue.length < 2) {
            return options.slice(0, Math.min(maxResults, 10));
        }
        
        const normalizedInput = normalizeText(inputValue);
        const words = normalizedInput.split(/\s+/).filter(word => word.length >= 2);
        
        if (words.length === 0) return options.slice(0, Math.min(maxResults, 10));
        
        const results = options
            .filter(option => {
                return searchFields.some(field => {
                    const fieldValue = normalizeText(typeof field === 'function' ? field(option) : option[field]);
                    if (!fieldValue) return false;
                    
                    // Solo mostrar si al menos una palabra coincide al inicio o como palabra completa
                    return words.some(word => {
                        return fieldValue.startsWith(word) || 
                               fieldValue.split(/\s+/).some(w => w.startsWith(word) || w === word);
                    });
                });
            })
            .slice(0, maxResults);
            
        return results;
    };
};

function GestionTiposAnalisis({ tipoAnalisis, handleNewAnalisis, handleClearTipoAnalisis }) {
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;

  // Función para validar y limpiar el contenido HTML permitiendo solo <p>, <b> e <i>
  const sanitizeHtml = (html) => {
    if (!html) return '';
    
    let cleanHtml = html;
    
    // Primero, proteger las etiquetas válidas temporalmente
    const protectedTags = {};
    let counter = 0;
    
    // Expresiones regulares para etiquetas válidas
    const validTagPatterns = [
      /<p\b[^>]*>/gi,      // <p> con o sin atributos
      /<\/p>/gi,           // </p>
      /<b\b[^>]*>/gi,      // <b> con o sin atributos  
      /<\/b>/gi,           // </b>
      /<i\b[^>]*>/gi,      // <i> con o sin atributos
      /<\/i>/gi            // </i>
    ];
    
    // Proteger etiquetas válidas con placeholders únicos
    validTagPatterns.forEach(pattern => {
      cleanHtml = cleanHtml.replace(pattern, (match) => {
        const placeholder = `__PROTECTED_TAG_${counter}__`;
        protectedTags[placeholder] = match;
        counter++;
        return placeholder;
      });
    });
    
    // Escapar todos los < y > restantes que no sean etiquetas válidas
    cleanHtml = cleanHtml
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Restaurar las etiquetas válidas protegidas
    Object.keys(protectedTags).forEach(placeholder => {
      cleanHtml = cleanHtml.replace(new RegExp(placeholder, 'g'), protectedTags[placeholder]);
    });
    
    // Limpiar cualquier etiqueta inválida que pueda haber quedado
    cleanHtml = cleanHtml.replace(/<(?!\/?(?:p|b|i)\b)[^>]*>/gi, '');
    
    // Decodificar entidades HTML en las etiquetas válidas para renderizado
    cleanHtml = cleanHtml
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'");
    
    return cleanHtml;
  };

  // Función para escapar caracteres especiales en texto plano
  const escapeHtmlChars = (text) => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  };

  // Función para insertar formato en el texto seleccionado
  const insertFormat = (tag) => {
    const textarea = document.getElementById('comentario-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = comentariorVR.substring(start, end);
    
    if (selectedText) {
      // Escapar el texto seleccionado antes de agregar las etiquetas
      const escapedText = escapeHtmlChars(selectedText);
      const beforeText = comentariorVR.substring(0, start);
      const afterText = comentariorVR.substring(end);
      
      let newText;
      if (tag === 'p') {
        newText = beforeText + `<p>${escapedText}</p>` + afterText;
      } else {
        newText = beforeText + `<${tag}>${escapedText}</${tag}>` + afterText;
      }
      
      setComentariorVR(newText);
      
      // Restaurar la selección después de un pequeño delay
      setTimeout(() => {
        const newEnd = start + `<${tag}>`.length + escapedText.length + `</${tag}>`.length;
        textarea.setSelectionRange(newEnd, newEnd);
        textarea.focus();
      }, 10);
    } else {
      // Si no hay texto seleccionado, insertar etiquetas vacías
      const beforeText = comentariorVR.substring(0, start);
      const afterText = comentariorVR.substring(end);
      
      let newText;
      if (tag === 'p') {
        newText = beforeText + `<p></p>` + afterText;
      } else {
        newText = beforeText + `<${tag}></${tag}>` + afterText;
      }
      
      setComentariorVR(newText);
      
      // Posicionar el cursor entre las etiquetas
      setTimeout(() => {
        const newPos = start + `<${tag}>`.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      }, 10);
    }
  };

  const [idAnalisis, SetIdAnalisis] = useState('');
  const [idMuestraxAnalisis, SetIdMuestraxAnalisis] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [claseAnalisis, setClaseAnalisis] = useState('');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [abreviatura, setAbreviatura] = useState('');
  const [alias, setAlias] = useState('');
  const [muestra, setMuestra] = useState('');
  const [orden, setOrden] = useState(0);
  const [decimales, setDecimales] = useState(0);
  const [integra400Plus, setIntegra400Plus] = useState('');
  const [cm250, setCm250] = useState('');
  const [cobasPure, setCobasPure] = useState('');
  const [nombreComercial, setNombreComercial] = useState('');
  const [grafica, setGrafica] = useState(false);
  const [adicional, setAdicional] = useState(false);
  const [remitido, setRemitido] = useState(false);
  const [ayuno, setAyuno] = useState(false);
  const [opcionImprimirNombre, setOpcionImprimirNombre] = useState(false);
  const [opcionImprimirMuestra, setOpcionImprimirMuestra] = useState(false);
  const [opcionImprimirMetodo, setOpcionImprimirMetodo] = useState(false);

  const { user } = useUser();
  const [tiposCondicion, setTiposCondicion] = useState([]);
  const [modulo, setModulo] = useState('');
  const [idAcceso, setIdAcceso] = useState(368);
  const [codigoAnalisis, setCodigoAnalisis] = useState(null);
  const [observacion, setobservacion] = useState('');
  const [muestras, setMuestras] = useState([]);
  const [condicion, setCondicion] = useState('');
  const [utilidad, setUtilidad] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [sexovr, setSexovr] = useState('');
  const [sexoe, setSexoe] = useState('');
  const [sexoesp, setSexoesp] = useState('');
  const [edadD, setEdadD] = useState('');
  const [edadH, setEdadH] = useState('');
  const [edadDS, setEdadDS] = useState('');
  const [edadHS, setEdadHS] = useState('');
  const [tituloVR, setTituloVR] = useState('');


  const [muestraAnalisis, setMuestraAnalisis] = useState([]);

  const [modulos, setModulos] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [clasesAnalisis, setClasesAnalisis] = useState([]);
  const [condiciones, setCondiciones] = useState([]);
  const [condicionesAnalisis, setCondicionesAnalisis] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [utilidadesAnalisis, setUtilidadesAnalisis] = useState([]);
  const [utilidades, setUtilidades] = useState([]);
  const [cantidades, setCantidades] = useState([]);
  const [sexos, setSexos] = useState([]);



  const [valoresEspecial, setValoresEspecial] = useState([]);
  const [valoresEdad, setValoresEdad] = useState([]);
  const [valoresSexo, setValoresSexo] = useState([]);
  const [valoresReferenciaMuestra, setValoresReferenciaMuestra] = useState([]);


  const [valorReferenciaS, setValorReferenciaS] = useState('');
  const [unidadMedidaS, setUnidadMedidaS] = useState('');
  const [equipoS, setEquipoS] = useState('');

  const [valorReferenciaES, setValorReferenciaES] = useState('');
  const [comentariorVR, setComentariorVR] = useState('');
  const [unidadMedidaES, setUnidadMedidaES] = useState('');
  const [parametroES, setParametroES] = useState('');
  const [muestraVR, setMuestraVR] = useState('');
  const [muestraC, setMuestraC] = useState('');
  const [muestraU, setMuestraU] = useState('');


  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const [tabIndex, setTabIndex] = useState(0); // Controla la pestaña principal
  const [subTabIndex, setSubTabIndex] = useState(0); // Controla las subpestañas dentro de "Valores de Referencia"

  // Estados para almacenar datos de los precios y valores de referencia
  const [tipoPrecio, setTipoPrecio] = useState('');
  const [precio, setPrecio] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [preciosPorSucursal, setPreciosPorSucursal] = useState([]);

  const [valorReferencia, setValorReferencia] = useState('');
  const [unidadMedida, setUnidadMedida] = useState('');
  const [equipo, setEquipo] = useState('');
  const [valoresUnicos, setValoresUnicos] = useState([]);
  const [valoresReferencia, setvaloresReferencia] = useState([]);
  // Estados para almacenar las opciones de los selects
  const [tipoPreciosOptions, setTipoPreciosOptions] = useState([]);
  const [sucursalesOptions, setSucursalesOptions] = useState([]);
  const [unidadMedidaOptions, setUnidadMedidaOptions] = useState([]);
  const [parametrosOptions, setParametrosOptions] = useState([]);

  const [equipoOptions, setEquipoOptions] = useState([]);
  const [titulosOptions, setTitulosOptions] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Estado para controlar la apertura del modal de muestra
  const [isMuestraModalOpen, setIsMuestraModalOpen] = useState(false);
  const [isParametroModalOpen, setIsParametroModalOpen] = useState(false);
  const [isCantidadModalOpen, setIsCantidadModalOpen] = useState(false);
  const [isTituloVRModalOpen, setIsTituloVRModalOpen] = useState(false);


  const [isUtilidadModalOpen, setIsUtilidadModalOpen] = useState(false);
  const [isCondicionModalOpen, setIsCondicionModalOpen] = useState(false);
  
  // Estados para edición de condiciones
  const [editingCondicion, setEditingCondicion] = useState(null);
  const [isEditingCondicion, setIsEditingCondicion] = useState(false);
  const [editingCondicionDescripcion, setEditingCondicionDescripcion] = useState(null);

  // Estados para edición de muestras
  const [editingMuestra, setEditingMuestra] = useState(null);
  const [isEditingMuestra, setIsEditingMuestra] = useState(false);

  // Estados para edición de utilidades
  const [editingUtilidad, setEditingUtilidad] = useState(null);
  const [isEditingUtilidad, setIsEditingUtilidad] = useState(false);
  const [editingUtilidadDescripcion, setEditingUtilidadDescripcion] = useState(null);

  const [preciosEliminados, setPreciosEliminados] = useState([]);
  const [valoresReferenciaEliminados, setValoresReferenciaEliminados] = useState([]);
  const [muestrasEliminadas, setMuestrasEliminadas] = useState([]);
  const [condicionesEliminadas, setCondicionesEliminadas] = useState([]);
  const [utilidadesEliminadas, setUtilidadesEliminadas] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // Para búsqueda
  const [openComentariosModal, setOpenComentariosModal] = useState(false);
  const [selectedAnalitoForComments, setSelectedAnalitoForComments] = useState('');
  const [editandoReferenciaId, setEditandoReferenciaId] = useState(null);

  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const [orderInput, setOrderInput] = useState('');

  // Estado para congelar el array filtrado mientras se arrastra
  const [draggedList, setDraggedList] = useState([]);
  const handleOpenComentarios = (valorReferencia) => {
    console.log(valorReferencia);
    // Supongamos que en tu fila, el campo "analito" se encuentra en valorReferencia.analito
    setSelectedAnalitoForComments(valorReferencia);
    setOpenComentariosModal(true);
  };

  const handleOpenOrderModal = (reference) => {
    setSelectedReference(reference);
    // Pre-cargar el input con el código actual
    setOrderInput(reference.CodigoAnalisis);
    setOpenOrderModal(true);
  };

  const handleCloseOrderModal = () => {
    setOpenOrderModal(false);
    setSelectedReference(null);
    setOrderInput('');
  };

  const handleSaveOrderChange = () => {
    const newOrder = parseInt(orderInput, 10);
    if (isNaN(newOrder)) {
      alert('Ingrese un valor numérico válido');
      return;
    }
    // Buscamos el otro análisis que ya tenga el nuevo código
    const otherAnalysis = valoresReferenciaMuestra.find(
      (item) => item.CodigoAnalisis === newOrder && item.id !== selectedReference.id
    );

    let updatedArray = [];
    if (otherAnalysis) {
      // Realizamos el intercambio: el análisis seleccionado tendrá newOrder 
      // y el otro obtendrá el código que tenía el seleccionado.
      const updatedSelected = { ...selectedReference, CodigoAnalisis: newOrder };
      const updatedOther = { ...otherAnalysis, CodigoAnalisis: selectedReference.CodigoAnalisis };

      updatedArray = valoresReferenciaMuestra.map((item) => {
        if (item.id === updatedSelected.id) return updatedSelected;
        if (item.id === updatedOther.id) return updatedOther;
        return item;
      });
    } else {
      // Si no existe otro análisis con ese código, simplemente actualizamos el código del seleccionado.
      updatedArray = valoresReferenciaMuestra.map((item) =>
        item.id === selectedReference.id ? { ...item, CodigoAnalisis: newOrder } : item
      );
    }

    setValoresReferenciaMuestra(updatedArray);
    setOpenOrderModal(false);
    setSelectedReference(null);
    setOrderInput('');
  };



  // Funciones para abrir y cerrar el modal
  const handleOpenUtilidadModal = () => {
    setEditingUtilidadDescripcion(null);
    setIsUtilidadModalOpen(true);
  };
  
  const handleCloseUtilidadModal = () => {
    setIsUtilidadModalOpen(false);
    setEditingUtilidadDescripcion(null);
  };


  // Funciones para abrir y cerrar el modal de muestra
  const handleOpenMuestraModal = () => setIsMuestraModalOpen(true);
  const handleCloseMuestraModal = () => setIsMuestraModalOpen(false);

  const handleOpenParametroModal = () => setIsParametroModalOpen(true);
  const handleCloseParametroModal = () => setIsParametroModalOpen(false);

  const handleOpenCantidadModal = () => setIsCantidadModalOpen(true);
  const handleCloseCantidadModal = () => setIsCantidadModalOpen(false);

  const handleOpenTituloVRModal = () => setIsTituloVRModalOpen(true);
  const handleCloseTituloVRModal = () => setIsTituloVRModalOpen(false);

  const handleOpenCondicionModal = () => {
    setEditingCondicion(null);
    setIsEditingCondicion(false);
    setIsCondicionModalOpen(true);
  };

  const handleEditCondicion = (condicion, index) => {
    // Buscar los datos completos de la condición en el array 'condiciones'
    const condicionCompleta = condiciones.find(c => c.idCondicion === condicion.idCondicion);
    
    if (condicionCompleta) {
      setEditingCondicion({ ...condicionCompleta, index });
      setIsEditingCondicion(true);
      setIsCondicionModalOpen(true);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos de la condición',
      });
    }
  };

  const handleCloseCondicionModal = () => {
    setIsCondicionModalOpen(false);
    setEditingCondicion(null);
    setIsEditingCondicion(false);
    setEditingCondicionDescripcion(null);
  };

  // Función para editar una muestra
  const handleEditMuestra = (muestra, index) => {
    setEditingMuestra(muestra);
    setIsEditingMuestra(true);
    setMuestra(muestra.idMuestra);
    setCantidad(muestra.cantidad);
    setobservacion(muestra.observacion || '');
  };

  // Función para cancelar la edición de muestra
  const handleCancelEditMuestra = () => {
    setEditingMuestra(null);
    setIsEditingMuestra(false);
    setMuestra('');
    setCantidad('');
    setobservacion('');
  };

  // Función para editar una utilidad
  const handleEditUtilidad = (utilidad, index) => {
    setEditingUtilidad({ ...utilidad, index });
    setIsEditingUtilidad(true);
    setUtilidad(utilidad.idUtilidad);
    setMuestraU(utilidad.idMuestra || muestraAnalisis.find(m => m.idMuestraXAnalisis === utilidad.idMuestraXAnalisis)?.idMuestra || '');
  };

  // Función para cancelar la edición de utilidad
  const handleCancelEditUtilidad = () => {
    setEditingUtilidad(null);
    setIsEditingUtilidad(false);
    setUtilidad('');
    setMuestraU('');
  };

  // Función para editar la descripción de una utilidad
  const handleEditUtilidadDescripcion = (utilidad) => {
    console.log('Utilidad a editar desde la tabla:', utilidad);
    // Buscar la utilidad completa en la lista de utilidades
    const utilidadCompleta = utilidades.find(u => u.idUtilidad === utilidad.idUtilidad);
    console.log('Utilidad completa encontrada:', utilidadCompleta);
    if (utilidadCompleta) {
      setEditingUtilidadDescripcion(utilidadCompleta);
      setIsUtilidadModalOpen(true);
    } else {
      alert('No se pudo encontrar la utilidad completa para editar');
    }
  };

  // Función para editar la descripción de una condición
  const handleEditCondicionDescripcion = (condicion) => {
    console.log('Condición a editar desde la tabla:', condicion);
    // Buscar la condición completa en la lista de condiciones
    const condicionCompleta = condiciones.find(c => c.idCondicion === condicion.idCondicion);
    console.log('Condición completa encontrada:', condicionCompleta);
    if (condicionCompleta) {
      setEditingCondicionDescripcion(condicionCompleta);
      setIsCondicionModalOpen(true);
    } else {
      alert('No se pudo encontrar la condición completa para editar');
    }
  };

  const handleUtilidadSaved = async () => {
  try {
    // Primero recargar la lista completa de utilidades
    const response = await fetch(`${server}/CdmUtilidades`);
    const data = await response.json();
    const nuevasUtilidades = data.$values;
    setUtilidades(nuevasUtilidades);
    
    // Actualizar la lista de utilidades del análisis con los datos actualizados
    if (editingUtilidadDescripcion) {
      const utilidadActualizada = nuevasUtilidades.find(u => u.idUtilidad === editingUtilidadDescripcion.idUtilidad);
      
      if (utilidadActualizada) {
        const updatedUtilidades = utilidadesAnalisis.map(ua => 
          ua.idUtilidad === editingUtilidadDescripcion.idUtilidad 
            ? { ...ua, utilidad: utilidadActualizada.descripcionUtilidad }
            : ua
        );
        setUtilidadesAnalisis(updatedUtilidades);
      }
    }
    
    setEditingUtilidadDescripcion(null);
    console.log('Utilidades actualizadas correctamente');
  } catch (error) {
    console.error('Error al recargar utilidades:', error);
  }
};

  // Función para refrescar la lista de condiciones después de editar
  const handleCondicionSaved = async () => {
    try {
      // Primero recargar la lista completa de condiciones
      const response = await fetch(`${server}/CdmCondicionesEspeciales`);
      const data = await response.json();
      const nuevasCondiciones = data.$values;
      setCondiciones(nuevasCondiciones);
      
      // Actualizar la lista de condiciones del análisis con los datos actualizados
      if (editingCondicionDescripcion) {
        const condicionActualizada = nuevasCondiciones.find(c => c.idCondicion === editingCondicionDescripcion.idCondicion);
        
        if (condicionActualizada) {
          const updatedCondiciones = condicionesAnalisis.map(ca => 
            ca.idCondicion === editingCondicionDescripcion.idCondicion 
              ? { ...ca, condicion: condicionActualizada.descripcionCondicion }
              : ca
          );
          setCondicionesAnalisis(updatedCondiciones);
        }
      }
      
      setEditingCondicionDescripcion(null);
      console.log('Condiciones actualizadas correctamente');
    } catch (error) {
      console.error('Error al recargar condiciones:', error);
    }
  };


  // Función para manejar el guardado de la muestra
  const handleSaveMuestra = async (nuevaMuestra) => {
    // Recargar la lista de muestras desde el servidor
    await fetchMuestra();
    
    Swal.fire({
      icon: 'success',
      title: 'Muestra Creada satisfactoriamente.',
      showConfirmButton: false,
      timer: 1500,
    });
    setMuestraAnalisis([...muestraAnalisis, nuevaMuestra]);
  };

  // Función para eliminar una muestra del catálogo general
  const handleDeleteMuestra = async (muestra) => {
    try {
      // Primero, verificar cuántos análisis usan esta muestra
      const usuarioBase = user?.codigoUsuario || 'ADMIN';
      const usuario = usuarioBase.padEnd(20, ' ');
      const usuarioEncoded = encodeURIComponent(usuario);
      
      // Intentar obtener información de uso (si el endpoint existe)
      let cantidadAnalisisAfectados = 0;
      let nombreAnalisis = [];
      let puedeVerificar = false;
      
      try {
        const responseUso = await fetch(`${server}/CdmTiposDeMuestras/${muestra.idMuestra}/uso`, {
          headers: { 'Content-Type': 'application/json' },
        });
        
        if (responseUso.ok) {
          const dataUso = await responseUso.json();
          cantidadAnalisisAfectados = dataUso.cantidad || 0;
          nombreAnalisis = dataUso.analisis || [];
          puedeVerificar = true;
        }
      } catch (errorUso) {
        console.warn('No se pudo verificar el uso de la muestra:', errorUso);
        // Continuar sin verificación
      }
      
      // Construir mensaje según el impacto
      let mensajeImpacto = '';
      let iconoAlerta = 'warning';
      let colorBoton = '#d33';
      let tituloAlerta = '¿Eliminar Muestra del Catálogo?';
      let textoBotonConfirmar = 'Sí, eliminar';
      let requiereConfirmacionEscrita = false;
      
      if (puedeVerificar && cantidadAnalisisAfectados > 0) {
        iconoAlerta = 'error';
        tituloAlerta = '🚨 ¡ADVERTENCIA CRÍTICA!';
        textoBotonConfirmar = `Sí, eliminar de ${cantidadAnalisisAfectados} análisis`;
        requiereConfirmacionEscrita = true;
        
        mensajeImpacto = `
          <div style="margin-top: 20px; padding: 15px; background-color: #f8d7da; border-left: 4px solid #dc3545; border-radius: 4px;">
            <p style="margin: 0; color: #721c24;"><strong>🚨 IMPACTO CRÍTICO:</strong></p>
            <ul style="margin: 10px 0 0 20px; color: #721c24; text-align: left;">
              <li>Esta muestra está siendo usada en <strong>${cantidadAnalisisAfectados} análisis diferente${cantidadAnalisisAfectados !== 1 ? 's' : ''}</strong></li>
              <li>Al eliminarla, estos análisis <strong>perderán esta muestra</strong> y todos sus datos asociados</li>
              <li>Se eliminarán <strong>TODAS las condiciones y utilidades</strong> relacionadas</li>
              <li>Se eliminarán <strong>TODOS los valores de referencia</strong> configurados para esta muestra</li>
              <li>Los resultados de pacientes con esta muestra mostrarán <strong>información incompleta</strong></li>
              <li>Las órdenes de trabajo pendientes podrían tener <strong>errores de procesamiento</strong></li>
              <li>Los reportes históricos perderán los rangos de referencia</li>
            </ul>
            ${nombreAnalisis.length > 0 ? `
              <div style="margin-top: 15px; text-align: left;">
                <p style="margin: 5px 0; font-weight: bold; color: #721c24;">📋 Análisis que se verán afectados:</p>
                <ul style="margin: 5px 0 0 20px; color: #721c24;">
                  ${nombreAnalisis.slice(0, 5).map(a => `<li>${a}</li>`).join('')}
                  ${nombreAnalisis.length > 5 ? `<li><em>...y ${nombreAnalisis.length - 5} más</em></li>` : ''}
                </ul>
              </div>
            ` : ''}
          </div>
        `;
      } else if (puedeVerificar && cantidadAnalisisAfectados === 0) {
        iconoAlerta = 'success';
        colorBoton = '#28a745';
        mensajeImpacto = `
          <div style="margin-top: 20px; padding: 15px; background-color: #d4edda; border-left: 4px solid #28a745; border-radius: 4px;">
            <p style="margin: 0; color: #155724;"><strong>✓ Seguro para eliminar:</strong></p>
            <p style="margin: 10px 0 0 0; color: #155724; text-align: left;">
              Esta muestra <strong>no está siendo utilizada</strong> en ningún análisis actualmente. 
              Es seguro eliminarla del catálogo.
            </p>
          </div>
        `;
      } else {
        // No se pudo verificar
        mensajeImpacto = `
          <div style="margin-top: 20px; padding: 15px; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
            <p style="margin: 0; color: #856404;"><strong>⚠️ ADVERTENCIA:</strong></p>
            <p style="margin: 10px 0 0 0; color: #856404; text-align: left;">
              No se pudo verificar el impacto de esta eliminación. 
              Esta muestra podría estar siendo usada en múltiples análisis.
              Se recomienda <strong>precaución extrema</strong> antes de continuar.
            </p>
            <ul style="margin: 10px 0 0 20px; color: #856404; text-align: left;">
              <li>Puede afectar análisis existentes</li>
              <li>Puede eliminar valores de referencia en uso</li>
              <li>Puede causar errores en órdenes de trabajo</li>
            </ul>
          </div>
        `;
      }
      
      const result = await Swal.fire({
        title: tituloAlerta,
        html: `
          <div style="text-align: center; margin-top: 15px;">
            <p style="font-size: 16px;"><strong>Muestra:</strong> ${muestra.descripcionMuestra || 'N/A'}</p>
            ${mensajeImpacto}
            <p style="margin-top: 25px; font-size: 16px; font-weight: bold;">
              ${cantidadAnalisisAfectados > 0 
                ? '⚠️ Esta es una acción IRREVERSIBLE.<br/>¿Está COMPLETAMENTE seguro?' 
                : '¿Está seguro de que desea eliminar esta muestra del catálogo?'}
            </p>
            ${requiereConfirmacionEscrita ? `
              <p style="margin-top: 15px; color: #721c24; font-size: 14px;">
                Para confirmar esta acción crítica, escriba <strong>ELIMINAR</strong> en el campo a continuación:
              </p>
            ` : ''}
          </div>
        `,
        icon: iconoAlerta,
        showCancelButton: true,
        confirmButtonColor: colorBoton,
        cancelButtonColor: '#3085d6',
        confirmButtonText: textoBotonConfirmar,
        cancelButtonText: 'Cancelar',
        focusCancel: true,
        width: '750px',
        ...(requiereConfirmacionEscrita && {
          input: 'text',
          inputPlaceholder: 'Escriba "ELIMINAR" para confirmar',
          inputAttributes: {
            style: 'text-align: center; font-size: 16px; text-transform: uppercase;'
          },
          preConfirm: (value) => {
            if (value !== 'ELIMINAR') {
              Swal.showValidationMessage('Debe escribir "ELIMINAR" para confirmar esta acción crítica');
              return false;
            }
            return true;
          }
        })
      });

      if (result.isConfirmed) {
        try {
          const response = await fetch(`${server}/CdmTiposDeMuestras/${muestra.idMuestra}/${usuarioEncoded}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          });

          if (response.ok) {
            // Recargar la lista de muestras desde el servidor
            await fetchMuestra();
            
            Swal.fire({
              icon: 'success',
              title: 'Muestra Eliminada del Catálogo',
              html: cantidadAnalisisAfectados > 0 
                ? `<p>La muestra <strong>"${muestra.descripcionMuestra}"</strong> ha sido eliminada.</p>
                   <p style="margin-top: 10px; color: #856404;">
                     <strong>Impacto:</strong> Se eliminó de ${cantidadAnalisisAfectados} análisis 
                     junto con todos sus valores de referencia, condiciones y utilidades asociadas.
                   </p>`
                : `<p>La muestra <strong>"${muestra.descripcionMuestra}"</strong> se eliminó correctamente del catálogo.</p>`,
              showConfirmButton: true,
              timer: 4000
            });
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al eliminar la muestra');
          }
        } catch (error) {
          console.error('Error al eliminar muestra:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error al Eliminar',
            html: `
              <p>No se pudo eliminar la muestra del catálogo.</p>
              <p style="margin-top: 10px; font-size: 14px; color: #721c24;">
                <strong>Posibles causas:</strong>
              </p>
              <ul style="text-align: left; margin: 10px 20px; color: #721c24;">
                <li>La muestra está siendo utilizada en análisis activos</li>
                <li>Existen valores de referencia vinculados</li>
                <li>Hay órdenes de trabajo pendientes</li>
                <li>Error de conexión con el servidor</li>
              </ul>
              <p style="margin-top: 10px; font-size: 14px;">
                Por favor, verifique las dependencias e intente nuevamente.
              </p>
            `,
            width: '600px'
          });
        }
      }
    } catch (error) {
      console.error('Error general al eliminar muestra:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error inesperado. Por favor, intente nuevamente.',
      });
    }
  };

  const handleSaveParametro = async (nuevaMuestra) => {
    await fetchParametros();
    Swal.fire({
      icon: 'success',
      title: 'Parametro Guardado satisfactoriamente.',
      showConfirmButton: false,
      timer: 1500,
    });

  };

  const handleSaveTitulo = async (nuevaMuestra) => {
    await fetchTitulos();
    Swal.fire({
      icon: 'success',
      title: 'Titulo Guardado satisfactoriamente.',
      showConfirmButton: false,
      timer: 1500,
    });

  };

  const handleDeleteTitulo = async (titulo) => {
    try {
      const result = await Swal.fire({
        title: '¿Eliminar Título?',
        html: `<p>¿Está seguro de eliminar el título <strong>"${titulo.titulo}"</strong>?</p>
               <p style="margin-top:10px;color:#856404;">Si este título está en uso no podrá ser eliminado.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });
      if (result.isConfirmed) {
        const usuarioBase = user?.codigoUsuario || 'ADMIN';
        const usuario = usuarioBase.padEnd(20, ' ');
        const usuarioEncoded = encodeURIComponent(usuario);
        const response = await fetch(`${server}/MgAnalitoValoresReferenciaTitulos/${titulo.id}/${usuarioEncoded}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          await fetchTitulos();
          Swal.fire({ icon: 'success', title: 'Título eliminado', showConfirmButton: false, timer: 1500 });
        } else {
          const errorData = await response.json().catch(() => ({}));
          Swal.fire({ icon: 'error', title: 'Error al eliminar', text: errorData.message || 'No se pudo eliminar el título' });
        }
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error inesperado.' });
    }
  };

  const handleEditTitulo = async (titulo) => {
    const result = await Swal.fire({
      title: 'Editar Título',
      input: 'text',
      inputLabel: 'Nuevo nombre',
      inputValue: titulo.titulo,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value?.trim()) return 'El título no puede estar vacío';
      },
    });
    if (result.isConfirmed) {
      try {
        const payload = { ...titulo, titulo: result.value.trim(), modificadoPor: user?.codigoUsuario || 'ADMIN' };
        const response = await fetch(`${server}/MgAnalitoValoresReferenciaTitulos/${titulo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          await fetchTitulos();
          Swal.fire({ icon: 'success', title: 'Título actualizado', showConfirmButton: false, timer: 1500 });
        } else {
          const errorData = await response.json().catch(() => ({}));
          Swal.fire({ icon: 'error', title: 'Error', text: errorData.message || 'No se pudo actualizar el título' });
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error inesperado.' });
      }
    }
  };

  const handleDeleteParametro = async (parametro) => {
    try {
      const result = await Swal.fire({
        title: '¿Eliminar Parámetro?',
        html: `<p>¿Está seguro de eliminar el parámetro <strong>"${parametro.analito}"</strong>?</p>
               <p style="margin-top:10px;color:#856404;">Si este parámetro está en uso no podrá ser eliminado.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });
      if (result.isConfirmed) {
        const usuarioBase = user?.codigoUsuario || 'ADMIN';
        const usuario = usuarioBase.padEnd(20, ' ');
        const usuarioEncoded = encodeURIComponent(usuario);
        const response = await fetch(`${server}/MgAnalitoes/${parametro.id}/${usuarioEncoded}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          await fetchParametros();
          Swal.fire({ icon: 'success', title: 'Parámetro eliminado', showConfirmButton: false, timer: 1500 });
        } else {
          const errorData = await response.json().catch(() => ({}));
          Swal.fire({ icon: 'error', title: 'Error al eliminar', text: errorData.message || 'No se pudo eliminar el parámetro' });
        }
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error inesperado.' });
    }
  };

  const handleEditParametro = async (parametro) => {
    const result = await Swal.fire({
      title: 'Editar Parámetro',
      input: 'text',
      inputLabel: 'Nuevo nombre',
      inputValue: parametro.analito,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value?.trim()) return 'El nombre del parámetro no puede estar vacío';
      },
    });
    if (result.isConfirmed) {
      try {
        const payload = { ...parametro, analito: result.value.trim(), modificadoPor: user?.codigoUsuario || 'ADMIN' };
        const response = await fetch(`${server}/MgAnalitoes/${parametro.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          await fetchParametros();
          Swal.fire({ icon: 'success', title: 'Parámetro actualizado', showConfirmButton: false, timer: 1500 });
        } else {
          const errorData = await response.json().catch(() => ({}));
          Swal.fire({ icon: 'error', title: 'Error', text: errorData.message || 'No se pudo actualizar el parámetro' });
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error inesperado.' });
      }
    }
  };

  // Función para manejar el cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Función para manejar el cambio de cantidad de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reiniciar a la primera página
  };


  const handleMoveUp = async (index) => {
    if (index === 0) return; // No se puede mover el primer elemento hacia arriba
    const newList = [...valoresReferenciaMuestra];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    // Actualiza la propiedad 'posicion' en el nuevo orden
    newList.forEach((item, idx) => item.posicion = idx + 1);
    setValoresReferenciaMuestra(newList);

  };

  const handleMoveDown = async (index) => {
    if (index === valoresReferenciaMuestra.length - 1) return; // No se puede mover el último hacia abajo
    const newList = [...valoresReferenciaMuestra];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    newList.forEach((item, idx) => item.posicion = idx + 1);
    setValoresReferenciaMuestra(newList);
  };



  // Fetch para obtener datos de las APIs y llenar los selectores
  useEffect(() => {
    const fetchTipoPrecios = async () => {
      try {
        const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisTiposDePrecios`);
        const data = await response.json();
        setTipoPreciosOptions(data.$values);
      } catch (error) {
        console.error('Error al cargar los tipos de precios:', error);
      }
    };

    const fetchSucursales = async () => {
      try {
        const response = await fetch(`${server}/MgSucursales`);
        const data = await response.json();
        setSucursalesOptions(data.$values);
      } catch (error) {
        console.error('Error al cargar las sucursales:', error);
      }
    };

    const fetchUnidadMedida = async () => {
      try {
        const response = await fetch(`${server}/MgUnidadesDeMedidums`);
        const data = await response.json();
        setUnidadMedidaOptions(data.$values);
      } catch (error) {
        console.error('Error al cargar las unidades de medida:', error);
      }
    };

    const fetchEquipo = async () => {
      try {
        const response = await fetch(`${server}/MgEquipoes`);
        const data = await response.json();
        setEquipoOptions(data.$values);
        console.log(data.$values);
      } catch (error) {
        console.error('Error al cargar los equipos:', error);
      }
    };

    const fetchCondiciones = async () => {
      try {
        const response = await fetch(`${server}/CdmCondicionesEspeciales`);
        const data = await response.json();
        setCondiciones(data.$values);
        console.log(data.$values);
      } catch (error) {
        console.error('Error al cargar los equipos:', error);
      }
    };

    const fetchUtilidades = async () => {
      try {
        const response = await fetch(`${server}/CdmUtilidades`);
        const data = await response.json();
        setUtilidades(data.$values);
        console.log(data.$values);
      } catch (error) {
        console.error('Error al cargar los equipos:', error);
      }
    };

    const fetchCantidades = async () => {
      try {
        const response = await fetch(`${server}/CdmCantidades`);
        const data = await response.json();
        setCantidades(data.$values);
      } catch (error) {
        console.error('Error al cargar los equipos:', error);
      }
    };

    const fetchSexos = async () => {
      try {
        const response = await fetch(`${server}/MgSexoes`);
        const data = await response.json();
        setSexos(data.$values);
      } catch (error) {
        console.error('Error al cargar los sexos:', error);
      }
    };


    // Llamadas a las funciones de fetch
    fetchTipoPrecios();
    fetchSucursales();
    fetchUnidadMedida();
    fetchEquipo();
    fetchMuestra();
    fetchCondiciones();
    fetchUtilidades();
    fetchCantidades();
    fetchSexos();
    fetchTipoCondiciones();
    fetchParametros();
    fetchTitulos();
  }, []);

  const fetchTitulos = async () => {
    try {
      const response = await fetch(`${server}/MgAnalitoValoresReferenciaTitulos`);
      const data = await response.json();
      setTitulosOptions(data.$values);
      console.log(data.$values);
    } catch (error) {
      console.error('Error al cargar los equipos:', error);
    }


  };

  const fetchParametros = async () => {
    try {
      const response = await fetch(`${server}/MgAnalitoes`);
      const data = await response.json();
      setParametrosOptions(data.$values);
    } catch (error) {
      console.error('Error al cargar las unidades de medida:', error);
    }
  };

  const fetchTipoCondiciones = async () => {
    try {
      const response = await fetch(`${server}/CdmCondicionesEspecialesTipos`);
      const data = await response.json();
      setTiposCondicion(data.$values);
      console.log(data.$values);
    } catch (error) {
      console.error('Error al cargar los tipos de condiciones:', error);
    }
  };

  const fetchMuestra = async () => {
    try {
      const response = await fetch(`${server}/CdmTiposDeMuestras`);
      const data = await response.json();
      setMuestras(Array.isArray(data.$values) ? data.$values : []);
      console.log(data.$values);
    } catch (error) {
      console.error('Error al cargar las muestras:', error);
    }
  };



  const fetchMuestraAnalisis = async (idAnalisis) => {
    try {
      const response = await fetch(`${server}/CdmMuestrasXAnalisis/porAnalisis/${idAnalisis}`);
      const data = await response.json();
      const muesAnalisis = data.$values;
      console.log('entro a muestras', muesAnalisis);
      // Combinamos los datos de ambas APIs
      const nuevasMuestras = muesAnalisis.map((mAnalisis) => ({

        idMuestraXAnalisis: mAnalisis.idMuestraXAnalisis,
        descripcionMuestra: muestras.find((mues) => mues.idMuestra === mAnalisis.idMuestra)?.descripcionMuestra,
        cantidad: mAnalisis.idCantidad,
        observacion: mAnalisis.observaciones,
        descripcionCantidad: cantidades.find((cant) => cant.idCantidad === mAnalisis.idCantidad)?.descripcionCantidad,
        idMuestra: mAnalisis.idMuestra,
        idAnalisis: idAnalisis
      }));
      console.log(nuevasMuestras);
      // Actualizamos el estado agregando los nuevos valores
      setMuestraAnalisis(nuevasMuestras);
    } catch (error) {
      console.error('Error al cargar las muestras:', error);
    }
  };

  const fetchCondicionesMuestra = async (idMuestraxAnalisis) => {
    try {
      console.log('esta en condiciones muestra: ' + idMuestraxAnalisis);
      const response = await fetch(`${server}/CdmCondicionesEspecialesXMuestraXAnalisis/poranalisis/${idMuestraxAnalisis}`);

      // Solo procesar si la respuesta es exitosa (status 200)
      if (response.ok) {
        const data = await response.json();
        console.log(data);

        let CondAnalisis = [];
        if (data && data.$values) {
          if (Array.isArray(data.$values)) {
            CondAnalisis = data.$values; // Si es un array, lo usamos directamente
          } else if (typeof data.$values === 'object') {
            CondAnalisis = [data.$values]; // Si es un objeto, lo convertimos en un array de un solo elemento
          } else {
            console.warn('Formato inesperado en la propiedad $values de la respuesta de la API.');
            return; // No podemos procesar este formato
          }
        } else if (data && typeof data === 'object') {
          CondAnalisis = [data]; // Si es un objeto, lo usamos como un array de un solo elemento
        } else {
          console.warn('Formato inesperado en la respuesta de la API.');
          return;
        }
        console.log(data.$values);
        // Combinamos los datos de ambas APIs
        const nuevasCondiciones = CondAnalisis.map((cAnalisis) => ({
          idCondicionXMuestraXAnalisis: cAnalisis.idCondicionXMuestraXAnalisis,
          idMuestraXAnalisis: cAnalisis.idMuestraXAnalisis,
          idCondicion: cAnalisis.idCondicion,
          condicion: condiciones.find((condi) => condi.idCondicion === cAnalisis.idCondicion)?.descripcionCondicion,
          muestra: cAnalisis.descripcionMuestra
        }));

        // Actualizamos el estado agregando los nuevos valores
        setCondicionesAnalisis((prevCondAnalisis => [...prevCondAnalisis, ...nuevasCondiciones]));
      } else {
        console.warn('La respuesta de la API no fue exitosa.');
      }
    } catch (error) {
      console.error('Error al cargar las condiciones por muestras:', error);
    }
  };

  const fetchUtilidadesMuestra = async (idUtilidadxAnalisis) => {
    try {
      const response = await fetch(`${server}/CdmUtilidadesXMuestraXAnalisis/porAnalisis/${idUtilidadxAnalisis}`);

      // Solo procesar si la respuesta es exitosa (status 200)
      if (response.ok) {
        const data = await response.json();

        // Verificamos si el resultado es un array
        let UtilAnalisis = [];

        if (data && data.$values) {
          if (Array.isArray(data.$values)) {
            UtilAnalisis = data.$values; // Si es un array, lo usamos directamente
          } else if (typeof data.$values === 'object') {
            UtilAnalisis = [data.$values]; // Si es un objeto, lo convertimos en un array de un solo elemento
          } else {
            console.warn('Formato inesperado en la propiedad $values de la respuesta de la API.');
            return; // No podemos procesar este formato
          }
        } else if (data && typeof data === 'object') {
          UtilAnalisis = [data]; // Si es un objeto, lo usamos como un array de un solo elemento
        } else {
          console.warn('Formato inesperado en la respuesta de la API.');
          return;
        }
        console.log(data.$values);
        // Combinamos los datos de ambas APIs
        const nuevasUtilidades = UtilAnalisis.map((uAnalisis) => ({
          idUtilidadXMuestraXAnalisis: uAnalisis.idUtilidadXMuestraXAnalisis,
          idMuestraXAnalisis: uAnalisis.idMuestraXAnalisis,
          idUtilidad: uAnalisis.idUtilidad,
          utilidad: utilidades.find((util) => util.idUtilidad === uAnalisis.idUtilidad)?.descripcionUtilidad,
        }));
        console.log(nuevasUtilidades);
        // Actualizamos el estado agregando los nuevos valores
        setUtilidadesAnalisis((prevUtilAnalisis => [...prevUtilAnalisis, ...nuevasUtilidades]));
      } else {
        console.warn('La respuesta de la API no fue exitosa.');
      }
    } catch (error) {
      console.error('Error al cargar las utilidades:', error);
    }
  };


  const fetchValoresReferenciaAnalito = async (idAnalisis) => {
    try {
      const response = await fetch(`${server}/MgAnalitoValoresReferenciums/GetByIdAnalisis/${idAnalisis}`);
      const data = await response.json();
      // Nos aseguramos de obtener un arreglo (o un arreglo vacío si no hay datos)
      const valoresReferencia = data.$values || [];
      console.log("Valores recibidos:", valoresReferencia);

      // Ordenamos el arreglo por el campo 'posicion'
      // Si 'posicion' puede ser undefined, se usa 0 por defecto
      const valoresOrdenados = valoresReferencia.sort(
        (a, b) => (a.posicion || 0) - (b.posicion || 0)
      );
      console.log("Valores ordenados:", valoresOrdenados);

      // Actualizamos el estado con los valores ya ordenados.
      setValoresReferenciaMuestra(valoresOrdenados);
    } catch (error) {
      console.error('Error al cargar los valores de referencia:', error);
    }
  };



  const handleAgregarPrecio = () => {
    if (!tipoPrecio || !precio || !sucursal) {
      alert('Por favor, completa todos los campos antes de agregar el precio.');
      return;
    }
    // Busca la descripción del tipo de precio seleccionada
    const tipoPrecioDescripcion = tipoPreciosOptions.find(
      (tipo) => tipo.idTipoPrecio === tipoPrecio
    )?.descripcionTipoPrecio;

    // Busca la descripción de la sucursal seleccionada
    const sucursalDescripcion = sucursalesOptions.find(
      (suc) => suc.idSucursal === sucursal
    )?.nombreSucursal;

    const sucursalCodigo = sucursalesOptions.find(
      (suc) => suc.idSucursal === sucursal
    )?.codigo;

    if (sucursalCodigo === 'TODAS') {
      // Filtrar la opción 'TODAS' si está presente en sucursalesOptions
      const sucursalesValidas = sucursalesOptions.filter(
        (suc) => suc.codigo !== 'TODAS'
      );

      // Agrega el precio para todas las sucursales válidas
      const preciosNuevos = sucursalesValidas.map((suc) => ({
        idPrecio: 0,
        descripcionTipoPrecio: tipoPrecioDescripcion,
        precio,
        nombreSucursal: suc.nombreSucursal,
        idTipoPrecio: tipoPrecio,
        idSucursal: suc.idSucursal,
      }));

      setPreciosPorSucursal([...preciosPorSucursal, ...preciosNuevos]);
    } else {
      // Busca la descripción de la sucursal seleccionada
      const sucursalDescripcion = sucursalesOptions.find(
        (suc) => suc.idSucursal === sucursal
      )?.nombreSucursal;

      // Agrega el nuevo precio con las descripciones
      setPreciosPorSucursal([
        ...preciosPorSucursal,
        {
          idPrecio: 0,
          descripcionTipoPrecio: tipoPrecioDescripcion,
          precio,
          nombreSucursal: sucursalDescripcion,
          idTipoPrecio: tipoPrecio,
          idSucursal: sucursal,
        },
      ]);
    }
    setTipoPrecio('');
    setPrecio('');
    setSucursal('');
  };

  const handleQuitarPrecio = (index) => {
    const precioEliminado = preciosPorSucursal[index];
    console.log(precioEliminado);
    // Solo agregar a la lista de eliminados si tiene un idPrecio (ya está en la base de datos)
    if (precioEliminado.idPrecio) {
      setPreciosEliminados([...preciosEliminados, precioEliminado]);
    }

    // Eliminar el precio de la tabla en pantalla
    const nuevaListaPrecios = [...preciosPorSucursal];
    nuevaListaPrecios.splice(index, 1);
    setPreciosPorSucursal(nuevaListaPrecios);
  };



  const handleQuitarValorReferencia = (id) => {
    const valorEliminado = valoresReferenciaMuestra.find(item => item.id === id);
    
    Swal.fire({
      title: '⚠️ ¿Eliminar Valor de Referencia?',
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <div style="background-color: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 15px;">
            <p style="margin: 5px 0;"><strong>Parámetro:</strong> ${valorEliminado?.analito || 'N/A'}</p>
            <p style="margin: 5px 0;"><strong>Valor de Referencia:</strong> ${valorEliminado?.valorReferencia || 'N/A'}</p>
            <p style="margin: 5px 0;"><strong>Unidad de Medida:</strong> ${valorEliminado?.unidadMedida || 'N/A'}</p>
            <p style="margin: 5px 0;"><strong>Muestra:</strong> ${valorEliminado?.descripcionMuestra || 'N/A'}</p>
            ${valorEliminado?.sexo ? `<p style="margin: 5px 0;"><strong>Sexo:</strong> ${valorEliminado.sexo}</p>` : ''}
            ${valorEliminado?.edadDesde || valorEliminado?.edadHasta ? 
              `<p style="margin: 5px 0;"><strong>Rango de Edad:</strong> ${valorEliminado.edadDesde || '0'} - ${valorEliminado.edadHasta || '∞'} años</p>` 
              : ''}
          </div>
          
          <div style="padding: 12px; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px; margin-top: 15px;">
            <p style="margin: 0 0 8px 0; color: #856404; font-weight: bold;">⚠️ ADVERTENCIA:</p>
            <ul style="margin: 5px 0 0 20px; color: #856404; line-height: 1.6;">
              <li>Este valor de referencia podría estar siendo usado en <strong>resultados de análisis existentes</strong></li>
              <li>Los <strong>reportes de pacientes</strong> que incluyan este parámetro podrían perder su rango de referencia</li>
              <li>Los <strong>resultados históricos</strong> podrían mostrar información incompleta</li>
              <li>Afectará la <strong>interpretación clínica</strong> de resultados previos</li>
              ${valorEliminado?.edadDesde || valorEliminado?.edadHasta ? 
                '<li>Se perderá la configuración específica por <strong>edad</strong></li>' : ''}
              ${valorEliminado?.sexo && valorEliminado.sexo !== 'N/A' ? 
                '<li>Se perderá la configuración específica por <strong>sexo</strong></li>' : ''}
            </ul>
          </div>
          
          <p style="margin-top: 20px; text-align: center; font-size: 15px; font-weight: bold;">
            ¿Está seguro de que desea eliminar este valor de referencia?
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
      width: '650px'
    }).then((result) => {
      if (result.isConfirmed) {
        // Buscar el elemento a eliminar basándose en su id
        if (valorEliminado && valorEliminado.id) {
          setValoresReferenciaEliminados([...valoresReferenciaEliminados, valorEliminado]);
        }
        // Filtrar el arreglo para eliminar el elemento con ese id
        const nuevaLista = valoresReferenciaMuestra.filter(item => item.id !== id);
        setValoresReferenciaMuestra(nuevaLista);
        
        Swal.fire({
          icon: 'success',
          title: 'Valor de Referencia Eliminado',
          text: 'El valor de referencia ha sido eliminado correctamente.',
          showConfirmButton: false,
          timer: 2000
        });
      }
    });
  };



  const handleQuitarMuestra = (index) => {
    const muestraEliminada = muestraAnalisis[index];
    console.log(muestraEliminada);
    
    // Contar cuántos valores de referencia están asociados a esta muestra
    const valoresAsociados = valoresReferenciaMuestra.filter(
      vr => vr.idMuestraxAnalisis === muestraEliminada.idMuestraxAnalisis
    );
    
    const cantidadValores = valoresAsociados.length;
    
    // Construir el mensaje de advertencia
    let mensajeAdvertencia = '';
    let mensajeDetalle = '';
    
    if (cantidadValores > 0) {
      mensajeAdvertencia = `Esta muestra tiene ${cantidadValores} valor${cantidadValores !== 1 ? 'es' : ''} de referencia asociado${cantidadValores !== 1 ? 's' : ''}.`;
      mensajeDetalle = `<div style="text-align: left; margin-top: 15px;">
        <p><strong>⚠️ ADVERTENCIA:</strong></p>
        <ul style="margin-left: 20px;">
          <li>Se eliminarán <strong>${cantidadValores} valor${cantidadValores !== 1 ? 'es' : ''} de referencia</strong> asociado${cantidadValores !== 1 ? 's' : ''} a esta muestra.</li>
          <li>Esta acción puede afectar <strong>resultados de análisis existentes</strong> que utilicen est${cantidadValores !== 1 ? 'os' : 'e'} valor${cantidadValores !== 1 ? 'es' : ''} de referencia.</li>
          <li>Los pacientes con análisis previos podrían perder sus rangos de referencia configurados.</li>
        </ul>
        <p style="margin-top: 10px; color: #d33;"><strong>¿Está seguro de que desea continuar?</strong></p>
      </div>`;
    } else {
      mensajeDetalle = '¿Está seguro de que desea eliminar esta muestra?';
    }

    Swal.fire({
      title: cantidadValores > 0 ? '⚠️ Eliminar Muestra con Valores de Referencia' : '¿Eliminar Muestra?',
      html: `
        <div style="text-align: center;">
          <p><strong>Muestra: ${muestraEliminada.descripcionMuestra || 'Sin descripción'}</strong></p>
          ${cantidadValores > 0 ? `<p style="margin-top: 10px;">${mensajeAdvertencia}</p>` : ''}
        </div>
        ${mensajeDetalle}
      `,
      icon: cantidadValores > 0 ? 'error' : 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: cantidadValores > 0 ? 'Sí, eliminar todo' : 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
      width: '600px'
    }).then((result) => {
      if (result.isConfirmed) {
        // Solo agregar a la lista de eliminados si tiene un idMuestra (ya está en la base de datos)
        if (muestraEliminada.idMuestra) {
          setMuestrasEliminadas([...muestrasEliminadas, muestraEliminada]);
        }

        // Eliminar los valores de referencia asociados a esta muestra
        if (cantidadValores > 0) {
          const valoresAEliminar = valoresAsociados
            .filter(vr => vr.idReferenciaXEdadSexo)
            .map(vr => ({ idReferenciaXEdadSexo: vr.idReferenciaXEdadSexo }));
          
          if (valoresAEliminar.length > 0) {
            setValoresReferenciaEliminados([...valoresReferenciaEliminados, ...valoresAEliminar]);
          }

          // Eliminar de la lista en pantalla
          const nuevosValoresReferencia = valoresReferenciaMuestra.filter(
            vr => vr.idMuestraxAnalisis !== muestraEliminada.idMuestraxAnalisis
          );
          setValoresReferenciaMuestra(nuevosValoresReferencia);
        }

        // Eliminar la muestra de la tabla en pantalla
        const nuevaListaMuestras = [...muestraAnalisis];
        nuevaListaMuestras.splice(index, 1);
        setMuestraAnalisis(nuevaListaMuestras);

        // Mostrar mensaje de confirmación
        Swal.fire({
          icon: 'success',
          title: 'Muestra Eliminada',
          text: cantidadValores > 0 
            ? `Se eliminó la muestra y ${cantidadValores} valor${cantidadValores !== 1 ? 'es' : ''} de referencia asociado${cantidadValores !== 1 ? 's' : ''}.`
            : 'La muestra se eliminó correctamente.',
          showConfirmButton: false,
          timer: 2000
        });
      }
    });
  };

  const handleQuitarCondicion = async (index) => {
    const condicionEliminada = condicionesAnalisis[index];
    const muestraAsociada = muestraAnalisis.find(
      m => m.idMuestraxAnalisis === condicionEliminada.idMuestraxAnalisis
    );
    
    Swal.fire({
      title: '⚠️ ¿Eliminar Condición?',
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <div style="background-color: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 15px;">
            <p style="margin: 5px 0;"><strong>Condición:</strong> ${condicionEliminada?.condicion || condicionEliminada?.descripcionCondicion || 'N/A'}</p>
            <p style="margin: 5px 0;"><strong>Muestra asociada:</strong> ${condicionEliminada?.muestra || muestraAsociada?.descripcionMuestra || 'N/A'}</p>
          </div>
          
          <div style="padding: 12px; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px; margin-top: 15px;">
            <p style="margin: 0 0 8px 0; color: #856404; font-weight: bold;">⚠️ ADVERTENCIA:</p>
            <ul style="margin: 5px 0 0 20px; color: #856404; line-height: 1.6;">
              <li>Esta condición define <strong>cuándo se debe solicitar</strong> esta muestra en el análisis</li>
              <li>Al eliminarla, podría afectar el <strong>flujo de trabajo</strong> del laboratorio</li>
              <li>Los técnicos podrían no saber bajo qué <strong>circunstancias procesar</strong> esta muestra</li>
              <li>Las <strong>órdenes de trabajo existentes</strong> con esta condición podrían mostrar información incompleta</li>
              <li>Se perderá la <strong>lógica de negocio</strong> asociada a esta muestra</li>
              <li>Puede impactar en la <strong>interpretación de resultados</strong> según el contexto clínico</li>
            </ul>
          </div>
          
          <p style="margin-top: 20px; text-align: center; font-size: 15px; font-weight: bold;">
            ¿Está seguro de que desea eliminar esta condición?
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
      width: '650px'
    }).then(async (result) => {
      if (result.isConfirmed) {
        console.log(condicionEliminada);

        const idRelacion = Number(condicionEliminada?.idCondicionXMuestraXAnalisis || 0);

        // Si ya existe en BD, eliminar inmediatamente para evitar que quede pendiente hasta "Guardar"
        if (idRelacion > 0) {
          try {
            const username = '' + user.codigoUsuario + '/';
            const deleteResponse = await fetch(`${server}/CdmCondicionesEspecialesXMuestraXAnalisis/${idRelacion}/${username}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            });

            if (!deleteResponse.ok) {
              const errorText = await deleteResponse.text().catch(() => '');
              Swal.fire({
                icon: 'error',
                title: `Error ${deleteResponse.status}`,
                text: errorText || 'No se pudo eliminar la condición en el servidor.'
              });
              return;
            }
          } catch (error) {
            console.error('Error al eliminar la condición:', error);
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Ocurrió un problema al eliminar la condición.'
            });
            return;
          }
        }

        // Eliminar la condición de la tabla en pantalla
        setCondicionesAnalisis((prev) => prev.filter((_, i) => i !== index));

        // Si estaba en cola de eliminados, la retiramos para evitar DELETE duplicado al guardar
        if (idRelacion > 0) {
          setCondicionesEliminadas((prev) =>
            prev.filter((c) => Number(c?.idCondicionXMuestraXAnalisis || 0) !== idRelacion)
          );
        }

        Swal.fire({
          icon: 'success',
          title: 'Condición Eliminada',
          text: 'La condición se eliminó correctamente del análisis.',
          showConfirmButton: false,
          timer: 2000
        });
      }
    });
  };

  const handleQuitarUtilidad = (index) => {
    const utilidadEliminada = utilidadesAnalisis[index];
    const muestraAsociada = muestraAnalisis.find(
      m => m.idMuestraxAnalisis === utilidadEliminada.idMuestraxAnalisis
    );
    
    Swal.fire({
      title: 'ℹ️ ¿Eliminar Utilidad?',
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <div style="background-color: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 15px;">
            <p style="margin: 5px 0;"><strong>Utilidad:</strong> ${utilidadEliminada?.descripcionUtilidad || 'N/A'}</p>
            <p style="margin: 5px 0;"><strong>Muestra asociada:</strong> ${muestraAsociada?.descripcionMuestra || 'N/A'}</p>
          </div>
          
          <div style="padding: 12px; background-color: #d1ecf1; border-left: 4px solid #17a2b8; border-radius: 4px; margin-top: 15px;">
            <p style="margin: 0 0 8px 0; color: #0c5460; font-weight: bold;">ℹ️ INFORMACIÓN:</p>
            <ul style="margin: 5px 0 0 20px; color: #0c5460; line-height: 1.6;">
              <li>Las utilidades describen <strong>para qué sirve</strong> esta muestra en el análisis</li>
              <li>Ayudan al personal a entender el <strong>propósito clínico</strong> de la muestra</li>
              <li>Son importantes para la <strong>documentación técnica</strong> y capacitación del personal</li>
              <li>Esta información podría aparecer en <strong>reportes internos</strong> o guías de procedimientos</li>
              <li>Facilitan la <strong>trazabilidad</strong> de por qué se solicita cada muestra</li>
              <li>Son útiles para la <strong>auditoría de calidad</strong> del laboratorio</li>
            </ul>
          </div>
          
          <p style="margin-top: 20px; text-align: center; font-size: 15px; font-weight: bold;">
            ¿Está seguro de que desea eliminar esta utilidad?
          </p>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
      width: '650px'
    }).then((result) => {
      if (result.isConfirmed) {
        console.log(utilidadEliminada);
        // Solo agregar a la lista de eliminados si tiene un idUtilidad (ya está en la base de datos)
        if (utilidadEliminada.idUtilidad) {
          setUtilidadesEliminadas([...utilidadesEliminadas, utilidadEliminada]);
        }

        // Eliminar la utilidad de la tabla en pantalla
        const nuevaListaUtilidades = [...utilidadesAnalisis];
        nuevaListaUtilidades.splice(index, 1);
        setUtilidadesAnalisis(nuevaListaUtilidades);
        
        Swal.fire({
          icon: 'success',
          title: 'Utilidad Eliminada',
          text: 'La utilidad se eliminó correctamente del análisis.',
          showConfirmButton: false,
          timer: 2000
        });
      }
    });
  };

  const handleSeleccionMuestra = (idMuestraxAnalisis) => {
    console.log("ID Muestra seleccionada: ", idMuestraxAnalisis);
    // Limpiar los arreglos antes de hacer las llamadas fetch
    setCondicionesAnalisis([]);  // Limpia las condiciones
    setUtilidadesAnalisis([]);   // Limpia las utilidades


    SetIdMuestraxAnalisis(idMuestraxAnalisis);
  };

  const handleAgregarMuestra = () => {
    if (!muestra || !cantidad) {
      alert('Por favor, completa todos los campos antes de agregar la muestra.');
      return;
    }
    // Busca la descripción del tipo de muestra seleccionada
    const muestraDescripcion = muestras.find(
      (mues) => mues.idMuestra === muestra
    )?.descripcionMuestra;

    const cantidadDescripcion = cantidades.find(
      (cant) => cant.idCantidad === cantidad
    )?.descripcionCantidad;

    if (isEditingMuestra && editingMuestra) {
      // Modo edición: actualizar la muestra existente
      const nuevaListaMuestras = muestraAnalisis.map((muestraItem) => {
        if (muestraItem.idMuestraXAnalisis === editingMuestra.idMuestraXAnalisis) {
          return {
            ...muestraItem,
            descripcionMuestra: muestraDescripcion,
            cantidad: cantidad,
            observacion: observacion,
            descripcionCantidad: cantidadDescripcion,
            idMuestra: muestra,
            isEdited: true // Marcar como editado para el guardado
          };
        }
        return muestraItem;
      });
      setMuestraAnalisis(nuevaListaMuestras);
      
      // Limpiar el estado de edición
      handleCancelEditMuestra();
    } else {
      // Modo agregar: crear nueva muestra
      setMuestraAnalisis([
        ...muestraAnalisis,
        {
          idMuestraXAnalisis: 0,
          descripcionMuestra: muestraDescripcion,
          cantidad: cantidad,
          observacion: observacion,
          descripcionCantidad: cantidadDescripcion,
          idMuestra: muestra
        },
      ]);
    }
    
    setMuestra('');
    setobservacion('');
    setCantidad('');
  };

  const handleGuardarMuestra = async () => {
    const analisisId = tipoAnalisis ? tipoAnalisis.idTipoAnalisis : idAnalisis;

    // Guardar las muestras asociadas al análisis
    for (const muestra of muestraAnalisis) {
      console.log(muestra);
      const datosMuestra = {
        idAnalisis: analisisId,
        idMuestra: muestra.idMuestra,
        idCantidad: muestra.cantidad,
        observaciones: (muestra.observacion),
        ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
      };

      if (muestra.isEdited && muestra.idMuestraXAnalisis) {
        // Actualizar muestra existente
        const datosActualizar = {
          idMuestraXAnalisis: muestra.idMuestraXAnalisis,
          ...datosMuestra,
          modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };
        
        const response = await fetch(`${server}/CdmMuestrasXAnalisis/${muestra.idMuestraXAnalisis}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(datosActualizar),
        });

        if (response.ok) {
          console.log('Muestra actualizada correctamente');
        } else {
          console.log(response);
          alert(`Error al actualizar muestra`);
        }
      } else if (!muestra.idMuestraXAnalisis || muestra.idMuestraXAnalisis === 0) {
        // Crear nueva muestra
        const response = await fetch(`${server}/CdmMuestrasXAnalisis`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(datosMuestra),
        });

        if (response.ok) {
          console.log('Muestra guardada correctamente');
        } else {
          console.log(response);
          alert(`Error al guardar muestra`);
        }
      }
    }

    const username = '' + user.codigoUsuario + '/';
    // Eliminar las muestras que el usuario eliminó
    for (const muestraEliminada of muestrasEliminadas) {
      console.log(muestraEliminada.idMuestraXAnalisis)
      await fetch(`${server}/CdmMuestrasXAnalisis/${muestraEliminada.idMuestraXAnalisis}/${username}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
    }

    fetchMuestraAnalisis(idAnalisis);
    // Limpiar arrays de eliminados y marcas de edición
    setMuestrasEliminadas([]);
    setMuestraAnalisis(prev => prev.map(muestra => ({ ...muestra, isEdited: false })));
    Swal.fire('Guardado', 'Las muestras se guardaron con exito.', 'success');
  };

  const handleGuardarCondicion = async () => {
    // Filtrar solo las condiciones nuevas (que aún no han sido guardadas)
    const conditionsToSave = condicionesAnalisis.filter(
      (condicion) => !condicion.idCondicionXMuestraXAnalisis || condicion.idCondicionXMuestraXAnalisis === 0
    );

    // Para cada condición nueva, enviamos la solicitud POST y actualizamos su ID en el estado
    for (const condicion of conditionsToSave) {
      const DatosCondicion = {
        idCondicionXMuestraXAnalisis: 0, // Se envía 0 para indicar que es nueva
        idMuestraXAnalisis: condicion.idMuestraXAnalisis || idMuestraxAnalisis,
        idCondicion: condicion.idCondicion,
        ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
      };

      console.log('Guardando condición:', DatosCondicion);
      const response = await fetch(`${server}/CdmCondicionesEspecialesXMuestraXAnalisis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(DatosCondicion)
      });

      if (response.ok) {
        const savedCondition = await response.json();
        console.log('Condición guardada correctamente:', savedCondition);
        // Actualizamos el estado para marcar la condición como guardada
        setCondicionesAnalisis((prev) =>
          prev.map((c) =>
            (!c.idCondicionXMuestraXAnalisis || c.idCondicionXMuestraXAnalisis === 0) &&
              c.idCondicion === condicion.idCondicion
              ? { ...c, idCondicionXMuestraXAnalisis: savedCondition.idCondicionXMuestraXAnalisis }
              : c
          )
        );
      } else {
        alert(`Error al guardar condiciones: ${response.statusText}`);
      }
    }
    const username = '' + user.codigoUsuario + '/';
    // Eliminar las condiciones que se han marcado para borrar
    for (const condicionEliminada of condicionesEliminadas) {
      const idRelacion = Number(condicionEliminada?.idCondicionXMuestraXAnalisis || 0);
      if (idRelacion <= 0) {
        continue;
      }

      console.log('Eliminando condición:', idRelacion);
      await fetch(`${server}/CdmCondicionesEspecialesXMuestraXAnalisis/${idRelacion}/${username}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
    }

    setCondicionesEliminadas([]);

    Swal.fire('Guardado', 'Las condiciones se guardaron con exito.', 'success');

  };



  const handleGuardarUtilidades = async () => {
    // Guardar las utilidades asociadas al análisis
    console.log(utilidadesAnalisis);
    for (const utilidad of utilidadesAnalisis) {
      if (utilidad.isEdited && utilidad.idUtilidadXMuestraXAnalisis) {
        // Actualizar utilidad existente
        const datosActualizar = {
          idUtilidadXMuestraXAnalisis: utilidad.idUtilidadXMuestraXAnalisis,
          idMuestraXAnalisis: utilidad.idMuestraXAnalisis,
          idUtilidad: utilidad.idUtilidad,
          modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };
        
        const response = await fetch(`${server}/CdmUtilidadesXMuestraXAnalisis/${utilidad.idUtilidadXMuestraXAnalisis}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(datosActualizar),
        });

        if (response.ok) {
          console.log('Utilidad actualizada correctamente');
        } else {
          console.log(response);
          alert(`Error al actualizar utilidad`);
        }
      } else if (!utilidad.idUtilidadXMuestraXAnalisis || utilidad.idUtilidadXMuestraXAnalisis === 0) {
        // Crear nueva utilidad
        const response = await fetch(`${server}/CdmUtilidadesXMuestraXAnalisis`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idUtilidadXMuestraXAnalisis: 0,
            idMuestraXAnalisis: utilidad.idMuestraXAnalisis ? utilidad.idMuestraXAnalisis : idMuestraxAnalisis,
            idUtilidad: utilidad.idUtilidad,
            ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
          }),
        });

        if (response.ok) {
          console.log('Utilidad guardada correctamente');
        } else {
          alert(`Error al guardar utilidad`);
        }
      }
    }
    
    const username = '' + user.codigoUsuario + '/';
    // Eliminar las utilidades que el usuario eliminó
    for (const utilidadeliminada of utilidadesEliminadas) {
      console.log(utilidadeliminada.idUtilidadXMuestraXAnalisis);
      await fetch(`${server}/CdmUtilidadesXMuestraXAnalisis/${utilidadeliminada.idUtilidadXMuestraXAnalisis}/${username}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
    }
    
    // Limpiar arrays de eliminados y marcas de edición
    setUtilidadesEliminadas([]);
    setUtilidadesAnalisis(prev => prev.map(utilidad => ({ ...utilidad, isEdited: false })));
    Swal.fire('Guardado', 'Las utilidades se guardaron con exito.', 'success');
  };


  const handleAgregarValorReferencia = () => {
    const camposRequeridos = [edadDS, edadHS, parametroES, muestraVR];
    const algunCampoVacio = camposRequeridos.some(v => v === undefined || v === null);

    if (algunCampoVacio) {
      console.log(edadDS, edadHS, parametroES, muestraVR);
      alert('Por favor, completa todos los campos antes de agregar o editar referencia.');
      return;
    }


    const DesParametro = parametrosOptions.find((param) => param.id === parametroES)?.analito;
    const DescUnidadMedida = unidadMedidaOptions.find((unidad) => unidad.idUnidadDeMedida === unidadMedidaES)?.descripcionUnidadDeMedida;
    const DescSexo = sexos.find((sex) => sex.idSexo === sexoe)?.descripcionSexo;
    const DescMuestra = muestras.find((mues) => mues.idMuestra === muestraVR)?.descripcionMuestra;
    const idMuestraxAnalisis = muestraAnalisis.find((mues) => mues.idMuestra === muestraVR)?.idMuestraXAnalisis;
    const DescEquipo = equipoOptions.find((equi) => equi.id === equipo)?.descripcion;
    const DescTitulo = titulosOptions.find((titu) => titu.id === tituloVR)?.titulo;

    const nuevaReferencia = {
      id: editandoReferenciaId || uuidv4(),
      idReferenciaXEdadSexo: editandoReferenciaId ? valoresReferenciaMuestra.find((vr) => vr.id === editandoReferenciaId)?.idReferenciaXEdadSexo || 0 : 0,
      valorReferencia: valorReferenciaES,
      descripcionUnidadDeMedida: DescUnidadMedida,
      idunidadMedida: unidadMedidaES,
      muestra: DescMuestra,
      idMuestra: muestraVR,
      descripcionSexo: DescSexo,
      edadMinima: edadDS,
      edadMaxima: edadHS,
      idSexo: sexoe,
      idParametro: parametroES,
      analito: DesParametro,
      descripcionMuestra: DescMuestra,
      idMuestraxAnalisis: idMuestraxAnalisis,
      esTemporal: editandoReferenciaId ? false : true,
      idAnalito: parametroES,
      adicional: adicional,
      descripcionEquipo: DescEquipo,
      idEquipo: equipo,
      comentario: comentariorVR,
      idtituloVR: tituloVR,
      titulo: DescTitulo,
      adicional: adicional,
      posicion: editandoReferenciaId
        ? valoresReferenciaMuestra.find((vr) => vr.id === editandoReferenciaId)?.posicion || 1
        : valoresReferenciaMuestra.length + 1
    };

    if (editandoReferenciaId) {
      // Modo edición: reemplazar
      setValoresReferenciaMuestra(valoresReferenciaMuestra.map((vr) =>
        vr.id === editandoReferenciaId ? nuevaReferencia : vr
      ));
    } else {
      // Modo nuevo: agregar
      setValoresReferenciaMuestra([...valoresReferenciaMuestra, nuevaReferencia]);
    }

    // Limpia el formulario
    setEditandoReferenciaId(null);
    setValorReferenciaES('');
    setUnidadMedidaES('');
    setEquipo('');
    setEdadDS('');
    setEdadHS('');
    setSexoe('');
    setMuestraVR('');
    setParametroES('');
    setAdicional(false);
    setComentariorVR('');
    setTituloVR('');
  };



  const handleAgregarCondicion = () => {

    if (!condicion) {
      alert('Por favor, completa todos los campos antes de agregar condicion.');
      return;
    }
    // Busca la descripción del tipo de precio seleccionada
    const condicionDescripcion = condiciones.find(
      (condi) => condi.idCondicion === condicion
    )?.descripcionCondicion;

    const idMuestraxAnalisis = muestraAnalisis.find(
      (mues) => mues.idMuestra === muestraC
    )?.idMuestraXAnalisis;

    const descripcionMuestra = muestraAnalisis.find(
      (mues) => mues.idMuestra === muestraC
    )?.descripcionMuestra;

    console.log('idMuestraXAnalisis', idMuestraxAnalisis)
    // Agrega la nueva condicion con las descripciones
    setCondicionesAnalisis([
      ...condicionesAnalisis,
      {
        idCondicionXMuestraXAnalisis: 0,
        idMuestraXAnalisis: idMuestraxAnalisis,
        idCondicion: condicion,
        condicion: condicionDescripcion,
        muestra: descripcionMuestra
      },
    ]);
    setCondicion('');
  };

  const handleAgregarUtilidad = () => {
    if (!utilidad) {
      alert('Por favor, completa todos los campos antes de agregar utilidad.');
      return;
    }
    
    // Busca la descripción del tipo de utilidad seleccionada
    const UtilidadDescripcion = utilidades.find(
      (util) => util.idUtilidad === utilidad
    )?.descripcionUtilidad;

    const idMuestraxAnalisis = muestraAnalisis.find(
      (mues) => mues.idMuestra === muestraU
    )?.idMuestraXAnalisis;

    if (isEditingUtilidad && editingUtilidad) {
      // Modo edición: actualizar la utilidad existente
      const nuevaListaUtilidades = utilidadesAnalisis.map((utilidadItem, index) => {
        if (index === editingUtilidad.index) {
          return {
            ...utilidadItem,
            idUtilidad: utilidad,
            utilidad: UtilidadDescripcion,
            idMuestraXAnalisis: idMuestraxAnalisis,
            isEdited: true // Marcar como editado para el guardado
          };
        }
        return utilidadItem;
      });
      setUtilidadesAnalisis(nuevaListaUtilidades);
      
      // Limpiar el estado de edición
      handleCancelEditUtilidad();
    } else {
      // Modo agregar: crear nueva utilidad
      setUtilidadesAnalisis([
        ...utilidadesAnalisis,
        {
          idUtilidadXMuestraXAnalisis: 0,
          idMuestraXAnalisis: idMuestraxAnalisis,
          idUtilidad: utilidad,
          utilidad: UtilidadDescripcion
        },
      ]);
    }
    
    setUtilidad('');
    setMuestraU('');
  };

  const handleSaveCondicion = async (condicion) => {
    try {
      // Verificar si estamos editando una condición (ya sea asociación o descripción)
      if ((isEditingCondicion && editingCondicion) || editingCondicionDescripcion) {
        const condicionAEditar = editingCondicion || editingCondicionDescripcion;
        
        // Actualizar condición existente - enviar todos los campos existentes + los modificados
        const response = await fetch(`${server}/CdmCondicionesEspeciales/${condicionAEditar.idCondicion}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...condicionAEditar,
            id_tipocondicion: condicion.id_tipocondicion,
            descripcionCondicion: condicion.descripcionCondicion,
            mostrarEnResultado: condicion.mostrarEnResultado,
            modificadoPor: condicion.modificadoPor
          }),
        });

        if (response.ok) {
          // Recargar la lista de condiciones desde el servidor
          const fetchCondiciones = async () => {
            try {
              const response = await fetch(`${server}/CdmCondicionesEspeciales`);
              const data = await response.json();
              setCondiciones(data.$values);
            } catch (error) {
              console.error('Error al cargar las condiciones:', error);
            }
          };
          await fetchCondiciones();
          
          // Actualizar condicionesAnalisis si la condición editada está en uso
          const updatedCondicionesAnalisis = condicionesAnalisis.map(ca => 
            ca.idCondicion === condicionAEditar.idCondicion 
              ? { ...ca, condicion: condicion.descripcionCondicion }
              : ca
          );
          setCondicionesAnalisis(updatedCondicionesAnalisis);

          Swal.fire({
            icon: 'success',
            title: 'Condición actualizada correctamente',
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          const errorData = await response.json().catch(() => null);
          console.error('Error al actualizar condición:', response.status, errorData);
          throw new Error('Error al actualizar la condición');
        }
      } else {
        // Crear nueva condición - comportamiento original
        const response = await fetch(`${server}/CdmCondicionesEspeciales`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(condicion),
        });

        if (response.ok) {
          // Recargar la lista de condiciones desde el servidor
          const fetchCondiciones = async () => {
            try {
              const response = await fetch(`${server}/CdmCondicionesEspeciales`);
              const data = await response.json();
              setCondiciones(data.$values);
            } catch (error) {
              console.error('Error al cargar las condiciones:', error);
            }
          };
          await fetchCondiciones();

          Swal.fire({
            icon: 'success',
            title: 'Condición creada correctamente',
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          throw new Error('Error al crear la condición');
        }
      }
    } catch (error) {
      console.error('Error al guardar condición:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar la condición',
        text: error.message,
      });
    }
  };
  useEffect(() => {
    if (tipoAnalisis) {
      console.log('Tipo análisis recibido:', tipoAnalisis);
      console.log('cobasPure value:', tipoAnalisis.cobasPure, 'CobasPure (PascalCase):', tipoAnalisis.CobasPure);
      console.log('idAcceso:', tipoAnalisis.idAcceso, 'IdAcceso:', tipoAnalisis.IdAcceso);
      console.log('codigoAnalisis:', tipoAnalisis.codigoAnalisis, 'CodigoAnalisis:', tipoAnalisis.CodigoAnalisis);
      handleCancelarEdicion();
      setDescripcion(tipoAnalisis.descripcionTipoAnalisis || '');
      setClaseAnalisis(tipoAnalisis.idClaseAnalisis || '');
      setCodigoBarras(tipoAnalisis.codigoDeBarras || '');
      setAbreviatura(tipoAnalisis.abreviatura || '');
      setAlias(tipoAnalisis.aliasAnalisis || '');
      setMuestra(tipoAnalisis.muestra || '');
      setOrden(tipoAnalisis.orden || 0);
      setDecimales(tipoAnalisis.decimales || 0);
      setIntegra400Plus(tipoAnalisis.integra400Plus || '');
      setCm250(tipoAnalisis.cm250 || '');
      setCobasPure(tipoAnalisis.cobasPure || tipoAnalisis.CobasPure || '');
      setNombreComercial(tipoAnalisis.nombreComercial || '');
      setGrafica(tipoAnalisis.grafica ?? false);
      setRemitido(tipoAnalisis.remitido ?? false);
      setAyuno(tipoAnalisis.ayuno ?? tipoAnalisis.Ayuno ?? false);
      setOpcionImprimirNombre(tipoAnalisis.opcionImprimirNombre ?? false);
      setOpcionImprimirMuestra(tipoAnalisis.opcionImprimirMuestra ?? false);
      setOpcionImprimirMetodo(tipoAnalisis.opcionImprimirMetodo ?? false);
      setModulo(tipoAnalisis.idModulo || tipoAnalisis.IdModulo || '');
      setIdAcceso(tipoAnalisis.idAcceso || tipoAnalisis.IdAcceso || 368);
      setCodigoAnalisis(tipoAnalisis.codigoAnalisis ?? tipoAnalisis.CodigoAnalisis ?? null);
      setIsEditing(true);
      SetIdAnalisis(tipoAnalisis.idTipoAnalisis);
      // Cargar condiciones del análisis existente
      fetchPreciosAnalisis(tipoAnalisis.idTipoAnalisis);
      fetchValorReferenciaXAnalisis(tipoAnalisis.idTipoAnalisis);
      fetchMuestraAnalisis(tipoAnalisis.idTipoAnalisis);
      fetchCondicionesMuestra(tipoAnalisis.idTipoAnalisis);  // Verificar si el valor es correcto
      fetchUtilidadesMuestra(tipoAnalisis.idTipoAnalisis);
    }

    // fetchTiposCondicion();
    fetchModulos();
    fetchClasesAnalisis();
  }, [tipoAnalisis]);



  const fetchPreciosAnalisis = async (idAnalisis) => {
    try {
      const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisPrecios/porAnalisis/${idAnalisis}`);
      const data = await response.json();
      setPreciosPorSucursal(Array.isArray(data.$values) ? data.$values : []);
      console.log(data.$values);
      console.log(idAnalisis);
    } catch (error) {
      console.error('Error al cargar los valores especiales:', error);
    }
  };

  const fetchValorReferenciaXAnalisis = async (idAnalisis) => {
    try {

      await fetchValoresReferenciaAnalito(idAnalisis);

    } catch (error) {
      console.error('Error al cargar los valores especiales:', error);
    }
  };

  const buildUpdatePayload = (valor) => {
    return {
      id: valor.id, // Suponiendo que en el objeto ya viene el id asignado
      valorReferencia: valor.valorReferencia ? valor.valorReferencia : ' ',
      edadMinima: valor.edadMinima,
      edadMaxima: valor.edadMaxima,
      idSexo: valor.idSexo ? valor.idSexo : null,
      modificadoPor: user ? user.codigoUsuario : 'ADMIN', // Reemplazamos ingresadoPor por modificadoPor
      idAnalito: valor.idAnalito,
      idMuestra: valor.idMuestra,
      posicion: valor.posicion ?? 1,
      ...(valor.idEquipo ? { idEquipo: valor.idEquipo } : {}),
      ...(valor.idunidadMedida && valor.idunidadMedida !== "" ? { idUnidadMedida: valor.idunidadMedida } : {}),
      comentario: valor.comentario ? valor.comentario : ' ',
      idTitulo: valor.idtituloVR ? valor.idtituloVR : null
    };
  };

  const updateReference = async (valor) => {
    const payload = buildUpdatePayload(valor);
    console.log(payload);
    try {
      const response = await fetch(`${server}/MgAnalitoValoresReferenciums/${valor.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        console.error('Error actualizando el registro con id:', valor.id);
      }
    } catch (error) {
      console.error('Error actualizando el registro con id:', valor.id, error);
    }
  };



  const handleGuardarValoresRef = async () => {
    if (!valoresReferenciaMuestra || valoresReferenciaMuestra.length === 0) {
      console.warn('No hay valores de referencia para guardar.');
      return;
    }

    for (const valor of valoresReferenciaMuestra) {
      try {
        // Si el valor es nuevo (temporal), hacemos POST
        if (valor.esTemporal === true) {
          const nuevoValor = {
            id: 0,
            valorReferencia: (valor.valorReferencia) || ' ',
            edadMinima: valor.edadMinima,
            edadMaxima: valor.edadMaxima,
            idSexo: valor.idSexo || null,
            ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
            idAnalito: valor.idAnalito,
            idMuestra: valor.idMuestraxAnalisis,
            posicion: valor.posicion ?? 1,
            ...(valor.idEquipo ? { idEquipo: valor.idEquipo } : {}),
            ...(valor.idunidadMedida && valor.idunidadMedida !== '' ? { idUnidadMedida: valor.idunidadMedida } : {}),
            comentario: valor.comentario || ' ',
            idTitulo: valor.idtituloVR || null,
            adicional: valor.adicional || null
          };
          console.log(nuevoValor);
          const response = await fetch(`${server}/MgAnalitoValoresReferenciums`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nuevoValor)
          });

          if (!response.ok) {
            console.error(`Error al crear referencia: ${response.statusText}`);
          } else {
            console.log('Referencia creada con éxito:', nuevoValor);
          }

                } else {
          // Si ya existe, hacemos PUT para actualizar
          const payload = {
            id: valor.id,
            valorReferencia: (valor.valorReferencia) || ' ',
            edadMinima: valor.edadMinima,
            edadMaxima: valor.edadMaxima,
            idSexo: valor.idSexo || null,
            modificadoPor: user ? user.codigoUsuario : 'ADMIN',
            idAnalito: valor.idAnalito,
            idMuestra: valor.idMuestraxAnalisis ? valor.idMuestraxAnalisis : valor.idMuestra,
            posicion: valor.posicion ?? 1,
            idEquipo: valor.idEquipo ? valor.idEquipo : null,
            // ...(valor.idEquipo ? { idEquipo: valor.idEquipo } : {}),
            // ...(valor.idunidadMedida && valor.idunidadMedida !== '' ? { idUnidadMedida: valor.idunidadMedida } : {}),
            idUnidadMedida: valor.idUnidadMedida ? valor.idUnidadMedida : valor.idunidadMedida ? valor.idunidadMedida : null,
            comentario: valor.comentario || ' ',
            idTitulo: valor.idtituloVR ? valor.idtituloVR : valor.idTitulo,
            adicional: valor.adicional || null
          };
          console.log(payload);
          const response = await fetch(`${server}/MgAnalitoValoresReferenciums/${valor.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            console.error(`Error actualizando valor con ID ${valor.id}: ${response.statusText}`);
          } else {
            console.log(`Valor con ID ${valor.id} actualizado correctamente`);
          }
        }

      } catch (error) {
        console.error('Error en la operación POST/PUT:', error);
      }
    }

    // Procesar eliminaciones
    if (valoresReferenciaEliminados.length > 0) {
      const username = `${user.codigoUsuario}/`;

      for (const eliminado of valoresReferenciaEliminados) {
        try {
          const response = await fetch(`${server}/MgAnalitoValoresReferenciums/${eliminado.id}/${username}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            console.error(`Error al eliminar valor con ID ${eliminado.id}`);
          } else {
            console.log(`Valor con ID ${eliminado.id} eliminado correctamente`);
          }

        } catch (error) {
          console.error('Error al hacer DELETE:', error);
        }
      }
    }

    fetchValorReferenciaXAnalisis(tipoAnalisis.idTipoAnalisis);

    Swal.fire({
      icon: 'success',
      title: 'Valores de Referencia Guardados.',
      showConfirmButton: false,
      timer: 1500,
    });

    setValoresReferenciaEliminados([]);
  };





  const handleCancelarEdicion = () => {
    setDescripcion('');
    setClaseAnalisis('');
    setCodigoBarras('');
    setAbreviatura('');
    setAlias('');
    setMuestra('');
    setOrden(0);
    setDecimales(0);
    setIntegra400Plus('');
    setCm250('');
    setCobasPure('');
    setNombreComercial('');
    setModulo('');
    setIdAcceso('');
    setCodigoAnalisis(null);
    setGrafica(false);
    setRemitido(false);
    setAyuno(false);
    setIsEditing(false);
    setPreciosPorSucursal([]);
    setValoresEdad([]);
    setValoresSexo([]);
    setValoresReferenciaMuestra([]);
    setValoresEspecial([]);
    setValoresUnicos([]);
    setMuestraAnalisis([]);
    setCondicionesAnalisis([]);
    setUtilidadesAnalisis([]);

  };

  const fetchCondiciones = async (analisisId) => {
    try {
      const response = await fetch(`${server}/CdmCondicionesEspeciales`);
      const data = await response.json();
      setCondiciones(data);
    } catch (error) {
      console.error('Error al cargar las condiciones:', error);
    }
  };

  const fetchModulos = async () => {
    try {
      const response = await fetch(`${server}/MgModuloes/porIdTipo/1`);
      const data = await response.json();
      setModulos(data.$values);
    } catch (error) {
      console.error('Error al cargar los módulos:', error);
    }
  };

  const fetchClasesAnalisis = async () => {
    try {
      const response = await fetch(`${server}/MgAnalisisGeneralClasesDeAnalisis`);
      const data = await response.json();
      setClasesAnalisis(data.$values);
    } catch (error) {
      console.error('Error al cargar las clases de análisis:', error);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();    const nuevoTipoAnalisis = {
      ...(isEditing && tipoAnalisis.idTipoAnalisis ? { IdTipoAnalisis: tipoAnalisis.idTipoAnalisis } : {}),
      IdAcceso: idAcceso,
      IdModulo: modulo,
      ...(codigoAnalisis !== null ? { CodigoAnalisis: codigoAnalisis } : {}),
      descripcionTipoAnalisis: (descripcion),
      idClaseAnalisis: claseAnalisis,
      codigoDeBarras: (codigoBarras || null),
      abreviatura: (abreviatura || null),
      aliasAnalisis: (alias || null),
      muestra: (muestra || null),
      orden: orden,
      decimales: decimales,
      integra400Plus: (integra400Plus || null),
      cm250: (cm250 || null),
      cobasPure: (cobasPure || null),
      nombreComercial: (nombreComercial || null),
      grafica: grafica ?? false,
      remitido: remitido ?? false,
      ayuno: ayuno ?? false,
      opcionImprimirNombre: opcionImprimirNombre ?? false,
      opcionImprimirMuestra: opcionImprimirMuestra ?? false,
      opcionImprimirMetodo: opcionImprimirMetodo ?? false,
      ...(isEditing ? {
        modificadoPor: user ? user.codigoUsuario : 'ADMIN',
        fechaModificacion: new Date().toISOString()
      } : {
        ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
        fechaIngreso: new Date().toISOString()
      }),
      modificacion: isEditing ? 2 : 1,
    };
    console.log(isEditing)
    try {
      console.log(nuevoTipoAnalisis);
      const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis${tipoAnalisis ? '/' + tipoAnalisis.idTipoAnalisis : ''}`, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoTipoAnalisis),
      });

      if (response.ok) {
        let data = null;

        try {
          data = await response.json();
        } catch (error) {
          console.warn('La respuesta no contiene JSON válido:', error);
        }

        const idTipoAnalisis = data ? (data.idTipoAnalisis || data.IdTipoAnalisis) : tipoAnalisis.idTipoAnalisis;

        SetIdAnalisis(idTipoAnalisis);
        console.log(idTipoAnalisis);
        // Guardar los precios nuevos
        for (const precio of preciosPorSucursal) {
          console.log('entro for');
          if (!precio.idPrecio) {
            console.log('entro if'); // Solo guardar si no tiene idPrecio (es nuevo)
            const nuevoPrecio = {
              idPrecio: 0,
              idTipoAnalisis: idTipoAnalisis,
              idTipoPrecio: precio.idTipoPrecio,
              precio: precio.precio,
              idSucursal: precio.idSucursal,
              ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
              // fechaIngreso: new Date().toISOString(),
              // modificacion: 1,
            };
            console.log(nuevoPrecio);
            const response1 = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisPrecios`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(nuevoPrecio),
            });
            console.log(response1);
          }
        }

        // Eliminar los precios que el usuario eliminó
        for (const precioEliminado of preciosEliminados) {
          console.log(precioEliminado.idPrecio)
          await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisPrecios/${precioEliminado.idPrecio}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          });
        }

        // Limpiar la lista de precios eliminados después de guardarlos
        setPreciosEliminados([]);

        Swal.fire({
          icon: 'success',
          title: tipoAnalisis ? 'Tipo de análisis actualizado con éxito' : 'Tipo de análisis creado con éxito',
          showConfirmButton: false,
          timer: 1500,
        });

        //alert(tipoAnalisis ? 'Tipo de análisis actualizado con éxito' : 'Tipo de análisis creado con éxito');
        if (isEditing) {
          handleCancelarEdicion();
        } else {
          console.log('entro a nuevo');
          setIsEditing(true);

        }
        handleNewAnalisis();
      } else {

        alert(`Error al ${tipoAnalisis ? 'actualizar' : 'crear'} el tipo de análisis`);
      }
    } catch (error) {
      console.error('Error al guardar el tipo de análisis:', error);
      alert('Error al guardar el tipo de análisis');
    }
  };
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filtrar valores de referencia según el campo de búsqueda
  // Filtrar valores de referencia según el campo de búsqueda
  const filteredValoresReferencia = valoresReferenciaMuestra.filter((valor) =>
    (valor.analito || '').toLowerCase().includes(searchQuery.toLowerCase()) || // Asegúrate de que analito exista
    (valor.valorReferencia || '').toLowerCase().includes(searchQuery.toLowerCase()) || // Asegúrate de que valorReferencia exista
    (valor.descripcionUnidadDeMedida || '').toLowerCase().includes(searchQuery.toLowerCase()) || // Asegúrate de que descripcionUnidadDeMedida exista
    (valor.descripcionSexo || '').toLowerCase().includes(searchQuery.toLowerCase()) // Asegúrate de que descripcionSexo exista
  );


  // Calcular datos visibles según la paginación
  const datosPaginados = filteredValoresReferencia.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Manejador de drag-and-drop
  const handleDragStart = () => {
    // Al iniciar el drag, congelamos la lista original
    setDraggedList(valoresReferenciaMuestra);
  };

  const handleDragEnd = (result) => {
    // Si no se soltó en un destino válido, limpiar y salir
    if (!result.destination) {
      setDraggedList([]);
      return;
    }

    // Usamos la lista congelada (no la filtrada ni la global que puede cambiar)
    const updatedList = Array.from(draggedList);
    const [movedItem] = updatedList.splice(result.source.index, 1);
    updatedList.splice(result.destination.index, 0, movedItem);

    // Actualizamos la propiedad "posicion" en la lista reordenada
    const reordered = updatedList.map((item, index) => ({
      ...item,
      posicion: index + 1, // Posición empezando en 1
    }));

    // Ahora actualizamos el estado global, buscando cada elemento por su id
    const updatedGlobal = valoresReferenciaMuestra.map((item) => {
      const updatedItem = reordered.find((r) => r.id === item.id);
      return updatedItem ? { ...item, posicion: updatedItem.posicion } : item;
    });

    setValoresReferenciaMuestra(updatedGlobal);
    // Limpiar la lista congelada al finalizar el drag
    setDraggedList([]);
  };

  return (
    <>
      {/* <div className="right_col" role="main"> */}
        {/* <div className="x_content"> */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} >
              <Grid item xs={12} md={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Módulo</InputLabel>
                  <Select
                    value={modulo}
                    onChange={(e) => setModulo(e.target.value)}
                    required
                  >
                    {modulos.map((modulo) => (
                      <MenuItem key={modulo.idModulo} value={modulo.idModulo}>
                        {modulo.nombreModulo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Decimales"
                  type="number"
                  value={decimales}
                  onChange={(e) => setDecimales(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Ordenamiento"
                  type="number"
                  value={orden}
                  onChange={(e) => setOrden(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={grafica}
                      onChange={(e) => setGrafica(e.target.checked)}
                    />
                  }
                  label="Gráfica"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={remitido}
                      onChange={(e) => setRemitido(e.target.checked)}
                    />
                  }
                  label="Remitido"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Analisis"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Comercial"
                  value={nombreComercial}
                  onChange={(e) => setNombreComercial(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Muestra BT1500"
                  value={muestra}
                  onChange={(e) => setMuestra(e.target.value)}
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Clase de Análisis</InputLabel>
                  <Select
                    value={claseAnalisis}
                    onChange={(e) => setClaseAnalisis(e.target.value)}
                    required
                  >
                    {clasesAnalisis.map((clase) => (
                      <MenuItem key={clase.idClaseAnalisis} value={clase.idClaseAnalisis}>{clase.descripcionClaseAnalisis}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ayuno}
                      onChange={(e) => setAyuno(e.target.checked)}
                    />
                  }
                  label="Ayuno"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Abreviatura"
                  value={abreviatura}
                  onChange={(e) => setAbreviatura(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alias de Análisis"
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>


              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Integra 400 Plus"
                  value={integra400Plus}
                  onChange={(e) => setIntegra400Plus(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="CM250"
                  value={cm250}
                  onChange={(e) => setCm250(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="COBAS C303"
                  value={cobasPure}
                  onChange={(e) => setCobasPure(e.target.value)}
                />
              </Grid>

              <h3>Precios</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Precio</InputLabel>
                    <Select
                      value={tipoPrecio}
                      onChange={(e) => setTipoPrecio(e.target.value)}
                    >
                      {tipoPreciosOptions.map((tipo) => (
                        <MenuItem key={tipo.idTipoPrecio} value={tipo.idTipoPrecio}>{tipo.descripcionTipoPrecio}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Precio"
                    type="number"
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Sucursal</InputLabel>
                    <Select
                      value={sucursal}
                      onChange={(e) => setSucursal(e.target.value)}
                    >
                      {sucursalesOptions.map((suc) => (
                        <MenuItem key={suc.idSucursal} value={suc.idSucursal}>{suc.nombreSucursal}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" onClick={handleAgregarPrecio} disabled={!tienePermiso('Agregar Precio Análisis')}>
                    Agregar Precio
                  </Button>
                </Grid>
              </Grid>

              <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }} >Tipo de Precio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} >Precio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} >Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell> {/* Columna para el botón de eliminar */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(preciosPorSucursal) && preciosPorSucursal.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((precio, index) => (
                      <TableRow key={index}>
                        <TableCell>{precio.descripcionTipoPrecio}</TableCell>
                        <TableCell>{precio.precio}</TableCell>
                        <TableCell>{precio.nombreSucursal}</TableCell>
                        <TableCell>
                          <IconButton color="secondary" onClick={() => handleQuitarPrecio(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                  </TableBody>
                </Table>
              </TableContainer>
              {/* Paginación */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={preciosPorSucursal.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              <Grid item xs={12}>
                <Button type="submit" variant="contained" style={{ backgroundColor: '#4CAF50', color: 'white' }} disabled={!tienePermiso('Crear Tipo Análisis')}>
                  {isEditing ? 'Actualizar' : 'Crear'} Tipo de Análisis
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelarEdicion}
                    style={{ marginLeft: '10px' }}
                  >
                    Cancelar Edición
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} md={12}>
                <Box>                  <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)} centered>
                    <Tab label="Muestras" />
                    <Tab label="Opciones de Impresión" />
                  </Tabs>
                                   {tabIndex === 0 && (

                    <Box p={3} sx={{ fontSize: '18px' }}>
                      <h3>Muestras</h3>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            fullWidth
                            options={Array.isArray(muestras) ? muestras : []} // Asegúrate de que muestras sea un array
                            getOptionLabel={(option) => option.descripcionMuestra || ''} // Manejar valores nulos o indefinidos
                            value={muestras.find((mues) => mues.idMuestra === muestra) || null}
                            onChange={(event, newValue) => setMuestra(newValue ? newValue.idMuestra : '')}
                            filterOptions={createSmartFilter(['descripcionMuestra'], 25)}
                            noOptionsText="No se encontraron muestras"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Muestra"
                                variant="outlined"
                                placeholder="Buscar muestra..."
                              />
                            )}
                            renderOption={(props, option, { inputValue }) => {
                              const searchText = normalizeText(inputValue);
                              const muestraText = normalizeText(option.descripcionMuestra || '');
                              const matches = muestraText.includes(searchText);
                              
                              return (
                                <li {...props} key={option.idMuestra} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '8px 16px'
                                }}>
                                  <Box component="span" sx={{ 
                                    fontWeight: matches ? 'bold' : 'normal',
                                    flex: 1
                                  }}>
                                    {option.descripcionMuestra}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evitar que se seleccione la opción
                                      handleDeleteMuestra(option);
                                    }}
                                    sx={{ 
                                      ml: 1,
                                      '&:hover': {
                                        backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                      }
                                    }}
                                    title="Eliminar muestra"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </li>
                              );
                            }}
                          />

                        </Grid>
                        <Grid item xs={12} md={1}>
                          <Button variant="contained" color="primary" onClick={handleOpenMuestraModal} disabled={!tienePermiso('Agregar Muestra Análisis')}>
                            +
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Cantidad:</InputLabel>
                            <Select
                              value={cantidad}
                              onChange={(e) => setCantidad(e.target.value)}
                            >
                              {cantidades.map((condi) => (
                                <MenuItem key={condi.idCantidad} value={condi.idCantidad}>{condi.descripcionCantidad}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={1} >
                          <Button variant="contained" color="primary" onClick={handleOpenCantidadModal} disabled={!tienePermiso('Agregar Muestra Análisis')}>
                            +
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <TextField
                            fullWidth
                            label="Observacion"
                            value={observacion}
                            onChange={(e) => setobservacion(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button variant="contained" color="primary" onClick={handleAgregarMuestra} disabled={!tienePermiso('Agregar Muestra Análisis')}>
                            {isEditingMuestra ? 'Actualizar muestra' : 'Agregar muestra'}
                          </Button>
                          {isEditingMuestra && (
                            <Button 
                              variant="outlined" 
                              color="secondary" 
                              onClick={handleCancelEditMuestra}
                              style={{ marginLeft: '10px' }}
                            >
                              Cancelar
                            </Button>
                          )}
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Muestra</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                                                                   <TableCell sx={{ fontWeight: 'bold' }}>Accion</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {muestraAnalisis.map((mue, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{mue.descripcionMuestra}</TableCell>
                                    <TableCell>{mue.descripcionCantidad}</TableCell>
                                    <TableCell>{mue.observacion}</TableCell>
                                    <TableCell>
                                      <IconButton color="primary" onClick={() => handleEditMuestra(mue, index)}>
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton color="secondary" onClick={() => handleQuitarMuestra(index)}>
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={12}>
                          <Button variant="contained" onClick={handleGuardarMuestra} style={{ backgroundColor: '#4CAF50', color: 'white' }} disabled={!tienePermiso('Guardar Muestra Análisis')}>
                            Guardar Muestra
                          </Button>
                        </Grid>
                      </Grid>
                      <br />
                      <Tabs value={subTabIndex} onChange={(e, newValue) => setSubTabIndex(newValue)} centered>
                        <Tab label="Condicion" />
                        <Tab label="Utilidad" />
                        <Tab label="Valores de Referencia" />
                      </Tabs>
                      {subTabIndex === 0 && (
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                              <Autocomplete
                                fullWidth
                                options={condiciones}
                                getOptionLabel={(option) => (option.tipoCondicion ? option.tipoCondicion + ' - ' : '') + option.descripcionCondicion} // Define la etiqueta a mostrar en el Autocomplete
                                value={condiciones.find((condi) => condi.idCondicion === condicion) || null} // Valor seleccionado
                                onChange={(event, newValue) => setCondicion(newValue ? newValue.idCondicion : '')} // Evento cuando cambia el valor
                                filterOptions={createSmartFilter(['descripcionCondicion', 'tipoCondicion'], 25)}
                                noOptionsText="No se encontraron condiciones"
                                renderInput={(params) => (
                                  <TextField 
                                    {...params} 
                                    label="Condición" 
                                    variant="outlined"
                                    placeholder="Buscar condición..."
                                  />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                  const searchText = normalizeText(inputValue);
                                  const condicionText = normalizeText(option.descripcionCondicion);
                                  const tipoText = normalizeText(option.tipoCondicion || '');
                                  const matches = condicionText.includes(searchText) || tipoText.includes(searchText);
                                  
                                  return (
                                    <li {...props} key={option.idCondicion}>
                                      <Box>
                                        {option.tipoCondicion && (
                                          <Box component="span" sx={{ 
                                            color: 'primary.main', 
                                            fontSize: '0.9em',
                                            fontWeight: tipoText.includes(searchText) ? 'bold' : 'normal'
                                          }}>
                                            {option.tipoCondicion} - 
                                          </Box>
                                        )}
                                        <Box component="span" sx={{ 
                                          fontWeight: condicionText.includes(searchText) ? 'bold' : 'normal',
                                          ml: option.tipoCondicion ? 1 : 0
                                        }}>
                                          {option.descripcionCondicion}
                                        </Box>
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <Button variant="contained" color="primary" onClick={handleOpenCondicionModal} disabled={!tienePermiso('Modificar Condiciones Análisis')}>
                                +
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Muestra</InputLabel>
                                <Select
                                  value={muestraC}
                                  onChange={(e) => setMuestraC(e.target.value)}
                                >
                                  {muestraAnalisis.map((equipo) => (
                                    <MenuItem key={equipo.idMuestra} value={equipo.idMuestra}>{equipo.descripcionMuestra}</MenuItem>
                                  ))}
                                </Select>


                              </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                              <Button variant="contained" color="primary" onClick={handleAgregarCondicion}>
                                Agregar condicion
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={12}>
                              <TableContainer
                                component={Paper}
                                sx={{ mt: 2, maxHeight: 300, overflowY: 'auto' }}
                              >
                                <Table stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Condición</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Muestra</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {condicionesAnalisis.map((condi, index) => (
                                      <TableRow key={index}>
                                        <TableCell>{condi.condicion}</TableCell>
                                        <TableCell>{condi.muestra}</TableCell>
                                        <TableCell>
                                          <IconButton
                                            color="info"
                                            onClick={() => handleEditCondicionDescripcion(condi)}
                                            title="Editar descripción de condición"
                                            size="small"
                                          >
                                            <Description />
                                          </IconButton>
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleEditCondicion(condi, index)}
                                            title="Editar asociación de condición"
                                            size="small"
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => handleQuitarCondicion(index)}
                                            title="Eliminar condición"
                                            size="small"
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                            <Grid item xs={12}>
                              <Button variant="contained" style={{ backgroundColor: '#4CAF50', color: 'white' }} onClick={handleGuardarCondicion} disabled={!tienePermiso('Modificar Condiciones Análisis')}>
                                Guardar Condiciones
                              </Button>

                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      {subTabIndex === 1 && (
                        <Box p={3}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                              <Autocomplete
                                fullWidth
                                options={utilidades} // El array de opciones
                                getOptionLabel={(option) => option.descripcionUtilidad} // Lo que se muestra en la lista desplegable
                                value={utilidades.find((util) => util.idUtilidad === utilidad) || null} // Valor seleccionado
                                onChange={(event, newValue) => setUtilidad(newValue ? newValue.idUtilidad : '')} // Manejo del cambio de valor
                                filterOptions={createSmartFilter(['descripcionUtilidad'], 30)}
                                noOptionsText="No se encontraron utilidades"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Utilidad" // Etiqueta del campo
                                    variant="outlined" // Estilo del campo
                                    placeholder="Buscar utilidad..."
                                  />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                  const searchText = normalizeText(inputValue);
                                  const utilidadText = normalizeText(option.descripcionUtilidad);
                                  const matches = utilidadText.includes(searchText);
                                  
                                  return (
                                    <li {...props} key={option.idUtilidad}>
                                      <Box component="span" sx={{ 
                                        fontWeight: matches ? 'bold' : 'normal'
                                      }}>
                                        {option.descripcionUtilidad}
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <Button variant="contained" color="primary" onClick={handleOpenUtilidadModal} disabled={!tienePermiso('Modificar Utilidades Análisis')}>
                                +
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Muestra</InputLabel>
                                <Select
                                  value={muestraU}
                                  onChange={(e) => setMuestraU(e.target.value)}
                                >
                                  {muestraAnalisis.map((equipo) => (
                                    <MenuItem key={equipo.idMuestra} value={equipo.idMuestra}>{equipo.descripcionMuestra}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleAgregarUtilidad}
                              >
                                {isEditingUtilidad ? 'Actualizar Utilidad' : 'Agregar Utilidad'}
                              </Button>
                            </Grid>
                            {isEditingUtilidad && (
                              <Grid item xs={3}>
                                <Button 
                                  variant="outlined" 
                                  color="secondary" 
                                  onClick={handleCancelEditUtilidad}
                                >
                                  Cancelar Edición
                                </Button>
                              </Grid>
                            )}
                            <Grid item xs={12} md={12}>
                              <TableContainer
                                component={Paper}
                                sx={{ mt: 2, maxHeight: 300, overflowY: 'auto' }}
                              >
                                <Table stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Utilidad</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {utilidadesAnalisis.map((util, index) => (
                                      <TableRow 
                                        key={index}
                                        sx={isEditingUtilidad && editingUtilidad?.index === index ? 
                                          { backgroundColor: '#e3f2fd' } : {}}
                                      >
                                        <TableCell>
                                          {util.utilidad}
                                          {isEditingUtilidad && editingUtilidad?.index === index && 
                                            <span style={{ color: '#1976d2', marginLeft: '8px' }}>(Editando)</span>
                                          }
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                          <IconButton
                                            color="info"
                                            onClick={() => handleEditUtilidadDescripcion(util)}
                                            title="Editar descripción de utilidad"
                                            size="small"
                                          >
                                            <Description />
                                          </IconButton>
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleEditUtilidad(util, index)}
                                            title="Editar asociación de utilidad"
                                            size="small"
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => handleQuitarUtilidad(index)}
                                            title="Eliminar utilidad"
                                            size="small"
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>

                            </Grid>

                            <Grid item xs={12} md={12}>
                              <Button variant="contained" style={{ backgroundColor: '#4CAF50', color: 'white' }} onClick={handleGuardarUtilidades} disabled={!tienePermiso('Modificar Utilidades Análisis')}>
                                Guardar Utilidades
                              </Button>

                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      {subTabIndex === 2 && (
                        <Box p={3} sx={{ fontSize: '18px' }}>
                          <h3>Valores de Referencia</h3>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={5}>
                              <Autocomplete
                                fullWidth
                                options={titulosOptions}
                                getOptionLabel={(option) => option.titulo || ''}
                                value={titulosOptions.find((t) => t.id === tituloVR) || null}
                                onChange={(_event, newValue) => setTituloVR(newValue ? newValue.id : '')}
                                filterOptions={createSmartFilter(['titulo'], 20)}
                                noOptionsText="No se encontraron títulos"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Título"
                                    variant="outlined"
                                    placeholder="Buscar título..."
                                  />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                  const searchText = normalizeText(inputValue);
                                  const tituloText = normalizeText(option.titulo || '');
                                  const matches = tituloText.includes(searchText);
                                  
                                  return (
                                    <li {...props} key={option.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px' }}>
                                      <Box component="span" sx={{ fontWeight: matches ? 'bold' : 'normal', flex: 1 }}>
                                        {option.titulo}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => { e.stopPropagation(); handleEditTitulo(option); }}
                                          sx={{ '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}
                                          title="Editar título"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteTitulo(option); }}
                                          sx={{ '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}
                                          title="Eliminar título"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={1}>
                              <Button variant="contained" color="primary" onClick={handleOpenTituloVRModal} disabled={!tienePermiso('Modificar Valores Referencia')}>
                                +
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Autocomplete
                                fullWidth
                                options={parametrosOptions} // Opciones para la unidad de medida
                                getOptionLabel={(option) => option.analito}
                                value={parametrosOptions.find((parametro) => parametro.id === parametroES) || null}
                                onChange={(event, newValue) => setParametroES(newValue ? newValue.id : '')}
                                filterOptions={createRestrictiveFilter(['analito'], 20)}
                                noOptionsText="No se encontraron parámetros"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Parametro"
                                    variant="outlined"
                                    placeholder="Buscar parámetro..."
                                  />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                  const matches = normalizeText(option.analito).includes(normalizeText(inputValue));
                                  return (
                                    <li {...props} key={option.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px' }}>
                                      <Box component="span" sx={{ fontWeight: matches ? 'bold' : 'normal', flex: 1 }}>
                                        {option.analito}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => { e.stopPropagation(); handleEditParametro(option); }}
                                          sx={{ '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}
                                          title="Editar parámetro"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteParametro(option); }}
                                          sx={{ '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}
                                          title="Eliminar parámetro"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={1}>
                              <Button variant="contained" color="primary" onClick={handleOpenParametroModal} disabled={!tienePermiso('Modificar Valores Referencia')}>
                                +
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label="Valor de Referencia"
                                value={valorReferenciaES}
                                onChange={(e) => setValorReferenciaES(e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Autocomplete
                                fullWidth
                                options={unidadMedidaOptions} // Opciones para la unidad de medida
                                getOptionLabel={(option) => option.descripcionUnidadDeMedida}
                                value={unidadMedidaOptions.find((unidad) => unidad.idUnidadDeMedida === unidadMedidaES) || null}
                                onChange={(event, newValue) => setUnidadMedidaES(newValue ? newValue.idUnidadDeMedida : '')}
                                filterOptions={createSmartFilter(['descripcionUnidadDeMedida'], 30)}
                                noOptionsText="No se encontraron unidades de medida"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Unidad de Medida"
                                    variant="outlined"
                                    placeholder="Buscar unidad..."
                                  />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                  const searchText = normalizeText(inputValue);
                                  const unidadText = normalizeText(option.descripcionUnidadDeMedida);
                                  const matches = unidadText.includes(searchText);
                                  
                                  return (
                                    <li {...props} key={option.idUnidadDeMedida}>
                                      <Box component="span" sx={{ 
                                        fontWeight: matches ? 'bold' : 'normal'
                                      }}>
                                        {option.descripcionUnidadDeMedida}
                                      </Box>
                                    </li>
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Edad Desde (días)"
                                value={edadDS}
                                onChange={(e) => setEdadDS(e.target.value)}
                                inputProps={{ min: 0, max: 1000000 }}
                                type="number"
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Edad Hasta (días)"
                                value={edadHS}
                                onChange={(e) => setEdadHS(e.target.value)}
                                inputProps={{ min: 0, max: 1000000 }}
                                type="number"
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth>
                                <InputLabel>Sexo</InputLabel>
                                <Select
                                  value={sexoe}
                                  onChange={(e) => setSexoe(e.target.value)}
                                >
                                  {sexos.map((sex) => (
                                    <MenuItem key={sex.idSexo} value={sex.idSexo}>{sex.descripcionSexo}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <FormControl fullWidth>
                                <InputLabel>Equipo</InputLabel>
                                <Select
                                  value={equipo}
                                  onChange={(e) => setEquipo(e.target.value)}
                                >
                                  {equipoOptions.map((equipo) => (
                                    <MenuItem key={equipo.id} value={equipo.id}>{equipo.descripcion}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12}>
                              <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                                Comentario
                              </Typography>
                              
                              {/* Botones de formato */}
                              <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<FormatBoldIcon />}
                                  onClick={() => insertFormat('b')}
                                >
                                  Negrita
                                </Button>
                                
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<FormatItalicIcon />}
                                  onClick={() => insertFormat('i')}
                                >
                                  Cursiva
                                </Button>
                                
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => insertFormat('p')}
                                >
                                  Párrafo
                                </Button>
                                
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    // Remover todas las etiquetas HTML y dejar solo texto plano
                                    const cleanText = comentariorVR.replace(/<[^>]*>/g, '');
                                    setComentariorVR(cleanText);
                                  }}
                                >
                                  Limpiar Formato
                                </Button>
                              </Box>
                              
                              {/* Textarea simple */}
                              <TextField
                                id="comentario-textarea"
                                fullWidth
                                multiline
                                rows={8}
                                value={comentariorVR}
                                onChange={(e) => {
                                  const cleanValue = sanitizeHtml(e.target.value);
                                  setComentariorVR(cleanValue);
                                }}
                                placeholder="Escriba su comentario aquí. Seleccione texto y use los botones para aplicar formato. Los símbolos < y > se manejarán automáticamente."
                                variant="outlined"
                                sx={{
                                  '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                    fontFamily: 'monospace'
                                  }
                                }}
                              />
                              
                              {/* Mensaje de ayuda */}
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                💡 <strong>Etiquetas permitidas:</strong> <code>&lt;p&gt;</code> para párrafos, <code>&lt;b&gt;</code> para negrita, <code>&lt;i&gt;</code> para cursiva
                                <br />
                                ⚠️ <strong>Símbolos especiales:</strong> Los símbolos &lt; y &gt; se convertirán automáticamente a &amp;lt; y &amp;gt; para evitar conflictos
                                <br />
                                🎯 <strong>Uso:</strong> Seleccione texto y use los botones de formato, o escriba las etiquetas directamente
                              </Typography>
                              
                              {/* Vista previa del HTML */}
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Vista previa:
                                </Typography>
                                <Box 
                                  sx={{ 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 1, 
                                    p: 2, 
                                    backgroundColor: '#f9f9f9',
                                    minHeight: '60px',
                                    '& b': { fontWeight: 'bold' },
                                    '& i': { fontStyle: 'italic' },
                                    '& p': { margin: '8px 0' }
                                  }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(comentariorVR) }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={adicional}
                                    onChange={(e) => setAdicional(e.target.checked)}
                                  />
                                }
                                label="Adicional"
                              />
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <FormControl fullWidth>
                                <InputLabel>Muestra</InputLabel>
                                <Select
                                  value={muestraVR}
                                  onChange={(e) => setMuestraVR(e.target.value)}
                                >
                                  {muestraAnalisis.map((muestra) => (
                                    <MenuItem key={muestra.idMuestra} value={muestra.idMuestra}>{muestra.descripcionMuestra}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                              <Button variant="contained" color="primary" onClick={handleAgregarValorReferencia} disabled={!tienePermiso('Modificar Valores Referencia')}>
                                Agregar Referencia
                              </Button>
                            </Grid>
                          </Grid>

                          <Grid item xs={12} md={12}>
                            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                              <Droppable droppableId="valoresReferenciaMuestra">
                                {(provided) => (
                                  <TableContainer
                                    component={Paper}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                                  >
                                    <Table>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Titulo</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Parámetros</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Valor Referencia</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Unidad de Medida</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Sexo</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Edad Desde (días)</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Edad Hasta (días)</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Equipo</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Comentario</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Adicional</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Muestra</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {valoresReferenciaMuestra.length === 0 ? (
                                          <TableRow>
                                            <TableCell colSpan={12} align="center">
                                              No se encontraron elementos
                                            </TableCell>
                                          </TableRow>
                                        ) : (
                                          valoresReferenciaMuestra.map((valor, index) => (
                                            <TableRow key={valor.id}>
                                              <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                  <IconButton onClick={() => handleMoveUp(index)} disabled={index === 0 || !tienePermiso('Modificar Valores Referencia')}>
                                                    <ArrowUpwardIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton onClick={() => handleMoveDown(index)} disabled={index === valoresReferenciaMuestra.length - 1 || !tienePermiso('Modificar Valores Referencia')}>
                                                    <ArrowDownwardIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton color="secondary" onClick={() => handleQuitarValorReferencia(valor.id)} disabled={!tienePermiso('Modificar Valores Referencia')}>
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton onClick={() => handleOpenComentarios(valor)} disabled={!tienePermiso('Modificar Valores Referencia')}>
                                                    <CommentIcon color={tienePermiso('Modificar Valores Referencia') ? "primary" : "disabled"} fontSize="small" />
                                                  </IconButton>
                                                  <IconButton
                                                    color="primary"
                                                    onClick={() => {

                                                      const muestraOriginalId = muestraAnalisis.find(
                                                        (m) => m.idMuestraXAnalisis === valor.idMuestra
                                                      )?.idMuestra;


                                                      setEditandoReferenciaId(valor.id);
                                                      setValorReferenciaES(valor.valorReferencia);
                                                      setUnidadMedidaES(valor.idunidadMedida ? valor.idunidadMedida : valor.idUnidadMedida);
                                                      setEquipo(valor.idEquipo);
                                                      setEdadDS(valor.edadMinima);
                                                      setEdadHS(valor.edadMaxima);
                                                      setSexoe(valor.idSexo);
                                                      setMuestraVR(muestraOriginalId);
                                                      setParametroES(valor.idAnalito);
                                                      setAdicional(valor.adicional);
                                                      setComentariorVR(valor.comentario);
                                                      setTituloVR(valor.idTitulo);
                                                    }}
                                                  >
                                                    <EditIcon fontSize="small" />
                                                  </IconButton>


                                                </Box>
                                              </TableCell>
                                              <TableCell>{valor.titulo}</TableCell>
                                              <TableCell>{valor.analito}</TableCell>
                                              <TableCell>{valor.valorReferencia}</TableCell>
                                              <TableCell>{valor.descripcionUnidadDeMedida}</TableCell>
                                              <TableCell>{valor.descripcionSexo}</TableCell>
                                              <TableCell>{valor.edadMinima}</TableCell>
                                              <TableCell>{valor.edadMaxima}</TableCell>
                                              <TableCell>{valor.equipo ? valor.equipo : valor.descripcionEquipo}</TableCell>
                                              {/* <TableCell>{valor.equipo }</TableCell> */}

                                              <TableCell>
                                                {valor.comentario ? (
                                                  <Box 
                                                    sx={{ 
                                                      maxWidth: '200px',
                                                      '& b': { fontWeight: 'bold' },
                                                      '& i': { fontStyle: 'italic' },
                                                      '& p': { margin: '4px 0' }
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: valor.comentario }}
                                                  />
                                                ) : (
                                                  '-'
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Checkbox checked={valor.adicional} disabled />
                                              </TableCell>
                                              <TableCell>{valor.descripcionMuestra}</TableCell>
                                            </TableRow>
                                          ))
                                        )}
                                      </TableBody>

                                    </Table>
                                  </TableContainer>
                                )}
                              </Droppable>
                            </DragDropContext>

                          </Grid>
                          <Grid item xs={12}>
                            <Button variant="contained" onClick={handleGuardarValoresRef} style={{ backgroundColor: '#4CAF50', color: 'white' }} disabled={!tienePermiso('Modificar Valores Referencia')}>
                              Guardar Referencias
                            </Button>
                          </Grid>
                        </Box>
                      )}
                    </Box>                  )}
                  
                  {tabIndex === 1 && (
                    <Box p={3} sx={{ fontSize: '18px' }}>
                      <h3>Opciones de Impresión</h3>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                          <Typography variant="h6" gutterBottom>
                            Configure las opciones de impresión para este análisis
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={opcionImprimirNombre}
                                onChange={(e) => setOpcionImprimirNombre(e.target.checked)}
                                disabled={!tienePermiso('Opciones Impresión Análisis')}
                              />
                            }
                            label="Imprimir Nombre"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={opcionImprimirMuestra}
                                onChange={(e) => setOpcionImprimirMuestra(e.target.checked)}
                                disabled={!tienePermiso('Opciones Impresión Análisis')}
                              />
                            }
                            label="Imprimir Muestra"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={opcionImprimirMetodo}
                                onChange={(e) => setOpcionImprimirMetodo(e.target.checked)}
                                disabled={!tienePermiso('Opciones Impresión Análisis')}
                              />
                            }
                            label="Imprimir Método"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={12} sx={{ mt: 3 }}>
                          <Alert severity="info">
                            Estas opciones determinan qué información se mostrará en los reportes impresos para este tipo de análisis.
                          </Alert>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                </Box>
              </Grid>
            </Grid>
          </form>

        {/* </div> */}
      {/* </div> */}
      {/* Integrar el modal */}
      <CondicionModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        handleSave={handleSaveCondicion}
        tiposCondicion={tiposCondicion}
        editingCondicion={null}
        isEditing={false}
      />
      <MuestraModal
        open={isMuestraModalOpen}
        handleClose={handleCloseMuestraModal}
        handleSave={handleSaveMuestra}
        usuarioActual={user?.codigoUsuario || 'ADMIN'}
      />
      <ParametroModal
        open={isParametroModalOpen}
        handleClose={handleCloseParametroModal}
        handleSave={handleSaveParametro}
        usuarioActual={user?.codigoUsuario || 'ADMIN'}
      />
      <UtilidadModal
        open={isUtilidadModalOpen}
        handleClose={handleCloseUtilidadModal}
        usuarioActual={user?.codigoUsuario || 'ADMIN'}
        utilidadToEdit={editingUtilidadDescripcion}
        onSave={handleUtilidadSaved}
      />
      <CondicionModal
        open={isCondicionModalOpen}
        handleClose={handleCloseCondicionModal}
        tiposCondicion={tiposCondicion}
        handleSave={handleSaveCondicion}
        editingCondicion={editingCondicion || editingCondicionDescripcion}
        isEditing={isEditingCondicion || !!editingCondicionDescripcion}
        onSave={handleCondicionSaved}
      />
      <CantidadModal
        open={isCantidadModalOpen}
        handleClose={handleCloseCantidadModal}
      />
      <TituloVRModal
        open={isTituloVRModalOpen}
        handleClose={handleCloseTituloVRModal}
        handleSave={handleSaveTitulo}
        usuarioActual={user?.codigoUsuario || 'ADMIN'}
      />
      {openComentariosModal && (
        <ComentariosModal
          open={openComentariosModal}
          onClose={() => setOpenComentariosModal(false)}
          Analito={selectedAnalitoForComments}
          onSave={(comentarios) => {

          }}
        />
      )}
    </>
  );

}

export default GestionTiposAnalisis;
